import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { ComplaintIDBadge, DeviceIDBadge, EmployeeIDBadge, StatusBadge, SeverityBadge } from '../../../shared/components/common/Badges';
import { AvailabilityImpactBadge, getAvailabilityImpactLabel } from '../../../shared/components/common/AvailabilityImpactBadge';
import { ContractCoverageBadge } from '../../../shared/components/common/ContractCoverageBadge';
import {
  getDeviceContractCoverageStatus,
  getDeviceContractValidityText,
} from '../../devices/utils/filterUtils';
import BarcodeScanner from '../../devices/components/BarcodeScanner';
import ComplaintTimeline from './ComplaintTimeline';
import { getDaysSinceNumeric } from '../../../utils/dateUtils';
import {
  getComplaintTatDetailRows,
  getComplaintTatPolicyTooltip,
  formatMinutesAsDuration,
  formatDueInstant,
  TAT_ESCALATION_NOTIFY_LINE,
} from '../../../utils/complaintTatFormatter';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import DocumentManager from '../../../shared/components/common/DocumentManager';
import ConfirmationModal from '../../../shared/components/modals/ConfirmationModal';
import ModalCloseButton from '../../../shared/components/modals/ModalCloseButton';
import {
  COMPLAINT_CLOSE_UPLOAD_DOCUMENT_OPTIONS,
  COMPLAINT_DOCUMENT_OPTIONS,
  COMPLAINT_DOCUMENT_TYPE_PHOTO,
  COMPLAINT_DOCUMENT_TYPE_REPORT,
} from '../../../shared/components/common/DocumentTypes';
import AnalyticsContextBanner from '../../analytics/components/AnalyticsContextBanner';
import StatusNotice from '../../../shared/components/common/StatusNotice';
import { getComplaintSeverityDescription } from '../constants/severityDescriptions';
import useBodyScrollLock from '../../../shared/hooks/useBodyScrollLock';
import { formatPhoneNumber, validateIndianPhoneNumber } from '../../../utils/phoneFormatter';
import { validateEmailAddress } from '../../../utils/emailValidator';
import './ComplaintView.css';

const sanitizeIndianPhoneInput = (value) => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length === 1 && digits === '0') return '';
  if (digits.length > 1 && digits.startsWith('0')) return '';
  return digits;
};

function isSystemComplaintNote(note) {
  const raw =
    note && note.note_type != null && String(note.note_type).trim() !== ''
      ? String(note.note_type).trim().toLowerCase()
      : 'user';
  return raw.startsWith('system');
}

function systemNoteActorLabel(noteType) {
  const t = (noteType || '').toLowerCase();
  if (t === 'system_complaint_creation') return 'Logged by';
  return 'Action by';
}

function ComplaintNoteAuthorHeader({ note, getUserNameByEmployeeId }) {
  if (isSystemComplaintNote(note)) {
    return (
      <div className="note-author-info note-author-info--system">
        <span className="note-author-icon note-author-icon--system" aria-hidden="true">
          ⚙️
        </span>
        <div className="note-author-stack">
          <span className="note-author-name">System</span>
          {note.posted_by_employee_id ? (
            <div
              className="note-system-actor-row"
              aria-label={`${systemNoteActorLabel(note.note_type)} ${getUserNameByEmployeeId(
                note.posted_by_employee_id,
                note.posted_by_full_name
              )} (${note.posted_by_employee_id})`}
            >
              <span className="note-system-actor-label">{systemNoteActorLabel(note.note_type)}</span>
              <span className="note-system-actor-name">
                {getUserNameByEmployeeId(note.posted_by_employee_id, note.posted_by_full_name)}
              </span>
              <span className="note-system-actor-id">{note.posted_by_employee_id}</span>
            </div>
          ) : null}
        </div>
      </div>
    );
  }
  return (
    <div className="note-author-info">
      <span className="note-author-icon">👤</span>
      <span className="note-author-name">
        {getUserNameByEmployeeId(note.posted_by_employee_id, note.posted_by_full_name)}
      </span>
      <span className="employee-id-badge">
        <span className="employee-id-icon">🆔</span>
        <span className="employee-id-value">{note.posted_by_employee_id}</span>
      </span>
    </div>
  );
}

const INCIDENT_SEVERITY_WEIGHTS = { critical: 1.0, major: 0.75, minor: 0.25 };

function computeIncidentDowntimeHours(complaint) {
  if (!complaint || !complaint.impacts_availability || complaint.status === 'cancelled') return 0;
  if (!complaint.breakdown_since) return 0;
  const breakdown = new Date(complaint.breakdown_since);
  let end = new Date();
  if (complaint.status === 'closed' && complaint.closed_at) {
    end = new Date(complaint.closed_at);
  }
  const hours = (end.getTime() - breakdown.getTime()) / 3600000;
  if (hours <= 0) return 0;
  const sev = (complaint.severity || 'minor').toLowerCase();
  const weight = INCIDENT_SEVERITY_WEIGHTS[sev] ?? 0.25;
  return Math.round(hours * weight * 10) / 10;
}

function formatReliabilityHours(totalHours) {
  if (totalHours === null || totalHours === undefined) return 'N/A';
  const hours = Number(totalHours);
  if (Number.isNaN(hours) || hours <= 0) return '0 d';
  const days = Math.floor(hours / 24);
  const rem = Math.round(hours % 24);
  if (days === 0) return `${rem}h`;
  if (rem === 0) return `${days}d`;
  return `${days}d ${rem}h`;
}

function getDeviceHealthLabel(status) {
  if (status === 'healthy') return 'Healthy';
  if (status === 'at_risk') return 'At Risk';
  if (status === 'critical') return 'Critical';
  return status || '—';
}

function getDeviceHealthColor(status) {
  if (status === 'healthy') return '#27ae60';
  if (status === 'at_risk') return '#f39c12';
  if (status === 'critical') return '#e74c3c';
  return '#6c757d';
}

function formatMtbfFromMetrics(m) {
  if (!m || (m.mtbf_days == null && m.mtbf_days_float == null)) return 'N/A';
  const totalDays = m.mtbf_days_float != null
    ? m.mtbf_days_float
    : (m.mtbf_days || 0) + (m.mtbf_hours || 0) / 24;
  if (totalDays <= 0) return 'N/A';
  const days = Math.floor(totalDays);
  const rem = Math.round((totalDays % 1) * 24);
  if (days === 0) return `${rem}h`;
  if (rem === 0) return `${days}d`;
  return `${days}d ${rem}h`;
}

/* ===== TOAST vs. BANNER MESSAGING STRATEGY (INDUSTRY-SPECIFIC) =====
 *
 * TOAST NOTIFICATIONS - Use for routine, non-blocking workflow steps:
 * - Assignment changes (standard workflow)
 * - Severity changes (routine classification update)
 * - Work started / resumed / escalated (status badge confirms new state)
 * - Acknowledgment and other normal status transitions
 * - Note posting (supportive documentation)
 * Characteristics: Corner popup, auto-dismisses (~2s), doesn't require user interaction
 * Medical Device Context: Routine activities that are logged automatically, not exceptional events
 *
 * STATUS NOTICE (persistent, no dismiss) - Use for ongoing contextual state:
 * - Terminal complaints (closed / cancelled — cannot be edited)
 * - View-only / read-only access warnings
 * Characteristics: Full-width, plain icon, stays until the underlying state changes
 *
 * ACTION BANNERS (StatusNotice + dismiss) - Use for ERRORS only:
 * - Authorization failures, workflow blocking errors, critical system issues
 * Characteristics: Full-width, requires dismissal, stays visible until user acts
 * Medical Device Context: These events are compliance-critical and must not be missed
 *
 * KEY PRINCIPLE - ERROR CATEGORIZATION:
 * Authorization errors (403) are ALWAYS shown as BANNERS with auditRelevant flag.
 * This categorization is medical device regulation requirement - access control attempts
 * must be tracked separately from technical failures.
 *
 * Errors MUST NEVER be consolidated or hidden. Each error type has different remediation:
 * - Authorization: Request reassignment or manager intervention
 * - Device Mismatch: Verify physical device against complaint record
 * - Verification Failed: Retry or contact IT support
 * - Status Transition Failed: System issue requiring investigation
 */

function DeviceVerifiedBanner({ scannedDeviceId }) {
  return (
    <StatusNotice variant="success" className="device-verified-status-notice">
      <span className="device-verified-inline">
        <span className="device-verified-title">Device verified</span>
        <DeviceIDBadge deviceId={scannedDeviceId || 'N/A'} size="small" />
      </span>
    </StatusNotice>
  );
}

function ComplaintView({
  complaintId,
  token,
  onClose,
  onUpdate,
  onViewFullDeviceDetails,
  isFullPage,
  user,
  readOnlyReviewMode = false,
  viewOnlyMessage = null,
}) {
  const [complaint, setComplaint] = useState(null);
  const [device, setDevice] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reporterBadgeHelpOpen, setReporterBadgeHelpOpen] = useState(false);

  // ===== ROLE-SPECIFIC PERMISSIONS FOR COMPLAINT MANAGEMENT =====
  // Custodian: Can view complaint, post notes (NO status changes, NO assign, NO severity changes)
  // Coordinator: Can view complaint, post notes (NO status changes, NO assign, NO severity changes, NO close/cancel) - VIEW ONLY
  // Department Manager: Can assign complaints, post notes, change severity before work starts (NO status changes, NO device scan)
  // Technician: Can scan device, close, cancel, post notes, change severity before work starts (NO assign — manager/CHD/admin only)
  // Central Help Desk: Can assign complaints, close, cancel, post notes, change severity before work starts (NO device scan)
  // Admin: Can do everything (create, assign, close, cancel, post notes, change severity)
  // SuperAdmin: Can do everything EXCEPT close/cancel (create, assign, post notes, change severity, NO close/cancel)
  // Note: Severity defaults from device-name criticality at create; can be adjusted after initial diagnosis while open/acknowledged

  const isCustodian = user && user.role === 'custodian';
  const isCoordinator = user && user.role === 'coordinator';
  const isTechnician = user && user.role === 'technician';
  const isManager = user && user.role === 'manager';
  const isAdmin = user && user.role === 'admin';
  const isSuperAdmin = user && user.role === 'superadmin';
  const isCentralHelpDesk = user && user.role === 'central_help_desk';

  const isTerminalComplaint =
    complaint?.status === 'closed' || complaint?.status === 'cancelled';

  // Permission flags for complaint operations (disabled in transfer preview or terminal status)
  const allowManage = !readOnlyReviewMode && !isTerminalComplaint;
  const canPostNotes =
    allowManage &&
    (isCustodian || isCoordinator || isTechnician || isManager || isAdmin || isSuperAdmin || isCentralHelpDesk);
  const canAssignComplaints =
    allowManage && (isManager || isAdmin || isSuperAdmin || isCentralHelpDesk);
  const canChangeSeverity =
    allowManage && (isTechnician || isManager || isAdmin || isSuperAdmin || isCentralHelpDesk);
  const canEditSeverity = canChangeSeverity && ['open', 'acknowledged'].includes(complaint?.status);
  const canScanDevice = allowManage && isTechnician;
  const employeeIdsMatch = (left, right) =>
    Boolean(left) &&
    Boolean(right) &&
    String(left).toLowerCase() === String(right).toLowerCase();
  const canAcknowledge =
    allowManage &&
    isTechnician &&
    Boolean(complaint?.assigned_employee_id) &&
    employeeIdsMatch(user?.employee_id, complaint?.assigned_employee_id);
  const isComplaintReporter = employeeIdsMatch(user?.employee_id, complaint?.reporter_employee_id);
  const isAssignedToSelf = employeeIdsMatch(user?.employee_id, complaint?.assigned_employee_id);
  const isAssignedToOtherTechnician =
    Boolean(complaint?.assigned_employee_id) && !isAssignedToSelf;
  // Common "you filed this" banner for any reporter; hide when the technician is also the assignee.
  const filedByYouVariant = (() => {
    if (!isComplaintReporter) return null;
    if (isTechnician && isAssignedToSelf) return null;
    if (isTechnician && isAssignedToOtherTechnician) return 'technician-blocked';
    if (isTechnician && !complaint?.assigned_employee_id) return 'technician-unassigned';
    return 'tracking';
  })();
  const canCloseComplaint = allowManage && complaint?.status === 'in_progress' && (isAdmin || isManager || isTechnician || isCentralHelpDesk);
  const canCancelComplaint = allowManage && complaint?.status !== 'escalated_to_vendor' && (isAdmin || isManager || isTechnician || isCentralHelpDesk);
  const canEscalateToVendor = allowManage && isTechnician
    && complaint?.status === 'in_progress'
    && device?.service_contract_type
    && device.service_contract_type !== 'In-house';
  const canResumeVendorWork = allowManage && isTechnician && complaint?.status === 'escalated_to_vendor';
  const canManageComplaints =
    canPostNotes ||
    canAssignComplaints ||
    canChangeSeverity ||
    canScanDevice ||
    canAcknowledge ||
    canCloseComplaint ||
    canCancelComplaint;
  const [newNote, setNewNote] = useState('');
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [showCloseForm, setShowCloseForm] = useState(false);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [closeDeviceScanned, setCloseDeviceScanned] = useState(false);
  const [cancelDeviceScanned, setCancelDeviceScanned] = useState(false);
  const [showCloseDeviceScan, setShowCloseDeviceScan] = useState(false);
  const [showCancelDeviceScan, setShowCancelDeviceScan] = useState(false);
  const [isEditingAssignment, setIsEditingAssignment] = useState(false);
  const [assignmentValue, setAssignmentValue] = useState('');
  const [showSeverityChangeModal, setShowSeverityChangeModal] = useState(false);
  const [severityModalValue, setSeverityModalValue] = useState('');
  const [severityChangeReason, setSeverityChangeReason] = useState('');
  const [severityModalError, setSeverityModalError] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [deviceExpanded, setDeviceExpanded] = useState(false);
  const [usersMap, setUsersMap] = useState({}); // Map of employee_id to user info for all users
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [technicians, setTechnicians] = useState([]); // Technicians for assignment dropdown
  const [loadingEngineers, setLoadingEngineers] = useState(false);
  const [deviceComplaintHistory, setDeviceComplaintHistory] = useState(null);
  const [deviceReliabilityMetrics, setDeviceReliabilityMetrics] = useState(null);
  const [showStartWorkModal, setShowStartWorkModal] = useState(false);
  const [startWorkDeviceScanned, setStartWorkDeviceScanned] = useState(false);
  const [showStartWorkDeviceScan, setShowStartWorkDeviceScan] = useState(false);
  const [startWorkImpactChoice, setStartWorkImpactChoice] = useState(null);
  const [isStartingWork, setIsStartingWork] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [noteAuthors, setNoteAuthors] = useState({}); // Maps employee_id to full_name
  const [expandedSections, setExpandedSections] = useState({
    deviceContext: false,
    reporterInfo: false,
    timelineMetrics: true
  });
  const [statusTimelineExpanded, setStatusTimelineExpanded] = useState(false);
  const [closeFormData, setCloseFormData] = useState({
    spare_parts_required: false,
    closing_remarks: '',
    hours_spent: 0
  });
  /** Complaint close: at least one complaint report uploaded after the document list baseline. */
  const [pastBaselineDocumentCount, setPastBaselineDocumentCount] = useState(0);
  const [deviceHistoryExpanded, setDeviceHistoryExpanded] = useState(false);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [resolutionExpanded, setResolutionExpanded] = useState(false);
  const [evidencePhotoCount, setEvidencePhotoCount] = useState(0);
  const [complaintReportCount, setComplaintReportCount] = useState(0);
  const [complaintDocsRefreshKey, setComplaintDocsRefreshKey] = useState(0);
  const [showKeepOpenConfirm, setShowKeepOpenConfirm] = useState(false);

  const complaintPhotoDocumentTypes = useMemo(
    () => [COMPLAINT_DOCUMENT_TYPE_PHOTO],
    []
  );
  const complaintReportDocumentTypes = useMemo(
    () => [COMPLAINT_DOCUMENT_TYPE_REPORT],
    []
  );
  const canDeleteComplaintReports = useMemo(
    () => ['admin', 'superadmin', 'coordinator', 'manager', 'technician'].includes(user?.role),
    [user?.role]
  );
  const [isScanning, setIsScanning] = useState(false);
  const [scannedDeviceId, setScannedDeviceId] = useState(null);
  const [scanError, setScanError] = useState(null);
  const [authError, setAuthError] = useState(null); // For authorization/permission errors
  const [statusTimeline, setStatusTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState(null);
  const [actionBanner, setActionBanner] = useState(null); // For success/error messages on status/assignment/severity changes
  const [expandedEscalationKey, setExpandedEscalationKey] = useState(null);
  const [showEscalateForm, setShowEscalateForm] = useState(false);
  const [showResumeConfirm, setShowResumeConfirm] = useState(false);
  const [isEscalating, setIsEscalating] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [escalationFormError, setEscalationFormError] = useState('');
  const [escalationForm, setEscalationForm] = useState({
    vendor_name: '',
    contact_person: '',
    email: '',
    phone: '',
    issue_summary: '',
    technician_findings: '',
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Sync expandedSections with isFullPage prop
  // When opening as full page, expand Reported By, and Device Context sections
  // When opening as modal, collapse them
  // Note: timelineMetrics is always expanded by default (independent of isFullPage)
  useEffect(() => {
    setExpandedSections(prev => ({
      ...prev,
      deviceContext: isFullPage,
      reporterInfo: isFullPage
      // timelineMetrics is NOT overridden - keeps its initial expanded state
    }));
  }, [isFullPage]);

  // Handle Escape key to close modal (only when modal, not fullpage)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !isFullPage) {
        onClose();
      }
    };

    if (!isFullPage) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullPage, onClose]);

  const shouldLockBodyScroll = !isFullPage || showCloseForm || showCancelForm || showStartWorkModal || showEscalateForm || showResumeConfirm || showKeepOpenConfirm;
  useBodyScrollLock(shouldLockBodyScroll);

  useEffect(() => {
    fetchComplaint();
    fetchDepartments();
    fetchAllUsers(); // Fetch all users to get note author names
    fetchStatusTimeline(); // Fetch status timeline
  }, [complaintId, token]);

  useEffect(() => {
    setReporterBadgeHelpOpen(false);
    setExpandedEscalationKey(null);
    setDeviceHistoryExpanded(false);
    setDetailsExpanded(false);
    setResolutionExpanded(false);
  }, [complaintId]);

  // Note: Technicians are now fetched in fetchComplaint(), so we don't need a separate useEffect

  // Reset close form state when modal opens/closes
  useEffect(() => {
    if (showCloseForm) {
      // Reset scan state when close form opens
      setCloseDeviceScanned(false);
      setShowCloseDeviceScan(false);
      setScanError(null);
      setScannedDeviceId(null);  // Clear scanned device ID
      // Reset close form data
      setCloseFormData({
        spare_parts_required: false,
        closing_remarks: '',
        hours_spent: 0
      });
      setPastBaselineDocumentCount(0);
    } else {
      // Also clear when form closes
      setScannedDeviceId(null);
    }
  }, [showCloseForm]);

  // Reset cancel form state when modal opens/closes
  useEffect(() => {
    if (showCancelForm) {
      // Reset scan state when cancel form opens
      setCancelDeviceScanned(false);
      setShowCancelDeviceScan(false);
      setScanError(null);
      setScannedDeviceId(null);  // Clear scanned device ID
      // Reset cancel reason
      setCancelReason('');
    } else {
      // Also clear when form closes
      setScannedDeviceId(null);
    }
  }, [showCancelForm]);

  // Reset start work modal state when it opens/closes
  useEffect(() => {
    if (showStartWorkModal) {
      setStartWorkDeviceScanned(false);
      setShowStartWorkDeviceScan(false);
      setScanError(null);
      setScannedDeviceId(null);
      setStartWorkImpactChoice(null);
      setIsStartingWork(false);
    } else {
      setScannedDeviceId(null);
    }
  }, [showStartWorkModal]);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.DEPARTMENTS.LIST, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchAllUsers = async () => {
    setLoadingUsers(true);
    try {
      // Fetch all users so we can look up any note author's info
      // Notes can be posted by any authenticated user, not just Technicians
      const response = await axios.get(API_ENDPOINTS.USERS.LIST, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Create a map of employee_id to user info for quick lookup
      const map = {};
      if (Array.isArray(response.data)) {
        response.data.forEach(user => {
          if (user.employee_id) {
            map[user.employee_id] = user;
          }
        });
      }
      setUsersMap(map);
    } catch (error) {
      console.error('Error fetching users:', error);
      // Continue without users map if fetch fails
    } finally {
      setLoadingUsers(false);
    }
  };

  const getUserNameByEmployeeId = (employeeId, postedByFullName) => {
    // First check if backend provided the full name directly (from note's posted_by_full_name)
    if (postedByFullName) return postedByFullName;

    // Fallback to usersMap (contains all users for note authors)
    const user = usersMap[employeeId];
    if (user && user.full_name) return user.full_name;

    // Return employee_id as last resort
    return employeeId;
  };

  const getAssignedEmployeeName = () => {
    // First, try to use the assigned_employee_name from complaint data (populated by backend)
    if (complaint && complaint.assigned_employee_name) {
      return complaint.assigned_employee_name;
    }

    // Fallback to looking up in usersMap
    if (complaint && complaint.assigned_employee_id) {
      return getUserNameByEmployeeId(complaint.assigned_employee_id);
    }

    return null;
  };

  const fetchStatusTimeline = async () => {
    setTimelineLoading(true);
    setTimelineError(null);
    try {
      const response = await axios.get(`${API_ENDPOINTS.COMPLAINTS.LIST}/${complaintId}/status-history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStatusTimeline(response.data.timeline || []);
    } catch (error) {
      console.error('Error fetching status timeline:', error);
      setTimelineError('Failed to load status timeline');
      // Continue without timeline if fetch fails
    } finally {
      setTimelineLoading(false);
    }
  };

  const fetchComplaint = async (showErrorToast = true) => {
    try {
      const response = await axios.get(`${API_ENDPOINTS.COMPLAINTS.LIST}/${complaintId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setComplaint(response.data);
      setAssignmentValue(response.data.assigned_employee_id || '');

      // Build noteAuthors map from notes
      if (response.data.notes && response.data.notes.length > 0) {
        const authorsMap = {};
        response.data.notes.forEach(note => {
          if (note.posted_by_employee_id && !authorsMap[note.posted_by_employee_id]) {
            authorsMap[note.posted_by_employee_id] = null; // Will be fetched if needed
          }
        });
        setNoteAuthors(authorsMap);
      }

      // Fetch device details to show status information
      if (response.data.device_id) {
        try {
          const deviceResponse = await axios.get(API_ENDPOINTS.DEVICES.GET(response.data.device_id), {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          setDevice(deviceResponse.data);

          // Fetch technicians for assignment dropdown from the device's location
          if (deviceResponse.data && deviceResponse.data.department_id && deviceResponse.data.location_id) {
            try {
              const techResponse = await axios.get(API_ENDPOINTS.USERS.BY_ROLE('technician', {
                department_id: deviceResponse.data.department_id,
                location_id: deviceResponse.data.location_id
              }), {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              setTechnicians(Array.isArray(techResponse.data) ? techResponse.data : []);
            } catch (techError) {
              console.error('Error fetching technicians:', techError);
              setTechnicians([]);
            }
          }

          // Fetch device complaint history
          try {
            const historyResponse = await axios.get(API_ENDPOINTS.DEVICES.COMPLAINT_HISTORY(response.data.device_id), {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            setDeviceComplaintHistory(historyResponse.data);

            try {
              const metricsResponse = await axios.get(
                API_ENDPOINTS.DEVICES.RELIABILITY_METRICS(response.data.device_id),
                { headers: { 'Authorization': `Bearer ${token}` } }
              );
              setDeviceReliabilityMetrics(metricsResponse.data);
            } catch (metricsError) {
              console.error('Error fetching device reliability metrics:', metricsError);
              setDeviceReliabilityMetrics(null);
            }
          } catch (historyError) {
            console.error('Error fetching device complaint history:', historyError);
            // Continue without history if fetch fails
          }
        } catch (deviceError) {
          console.error('Error fetching device details:', deviceError);
          // Continue without device details if fetch fails
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching complaint:', error);
      if (showErrorToast) {
        toast.error('Failed to load complaint details');
      }
      setLoading(false);
    }
  };

  const handleSaveAssignment = async () => {
    try {
      await axios.put(`${API_ENDPOINTS.COMPLAINTS.LIST}/${complaintId}`, {
        assigned_employee_id: assignmentValue || null
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Fetch updated complaint data - this will update state and get assigned_employee_name from backend
      const updatedResponse = await axios.get(`${API_ENDPOINTS.COMPLAINTS.LIST}/${complaintId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Get the name directly from the fresh response data rather than relying on state update timing
      const assignedName = assignmentValue
        ? (updatedResponse.data.assigned_employee_name || assignmentValue)
        : 'Unassigned';

      // Now update component state with fresh data
      setComplaint(updatedResponse.data);
      setAssignmentValue(updatedResponse.data.assigned_employee_id || '');
      await fetchStatusTimeline(); // Refresh timeline after assignment change
      if (onUpdate) onUpdate();
      setIsEditingAssignment(false);

      // Show toast notification for routine, non-critical update
      // Toast is appropriate here because assignment change is a standard workflow step, not exceptional
      toast.success(`Assigned to ${assignedName}`);
    } catch (error) {
      console.error('Error updating assignment:', error);
      let errorMessage = 'Failed to update assignment. Please try again.';

      // Check for specific error types
      if (error.response?.status === 403) {
        // Authorization error: User lacks permission to change assignment
        // Medical device context: Access control violation for complaint modification
        errorMessage = 'You are not allowed to change the assignment for this complaint.';
        // Use banner for authorization errors (must be visible and not missed)
        setActionBanner({
          type: 'error',
          title: '🔒 Not Authorized',
          message: errorMessage,
          severity: 'high',
          auditRelevant: true
        });
        return;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Generic assignment errors: Use banner to ensure visibility
      // Medical device context: Assignment changes are critical for workflow tracking
      setActionBanner({
        type: 'error',
        title: '❌ Assignment Update Failed',
        message: errorMessage,
        severity: 'medium'
      });
    }
  };

  const handleCancelAssignment = () => {
    setAssignmentValue(complaint.assigned_employee_id || '');
    setIsEditingAssignment(false);
  };

  const openSeverityChangeModal = () => {
    if (!complaint) return;
    setSeverityModalValue(complaint.severity || 'minor');
    setSeverityChangeReason('');
    setSeverityModalError('');
    setShowSeverityChangeModal(true);
  };

  const closeSeverityChangeModal = () => {
    setShowSeverityChangeModal(false);
    setSeverityModalError('');
  };

  const handleConfirmSeverityChange = async () => {
    if (!complaint) return;
    const reason = (severityChangeReason || '').trim();
    const currentSev = complaint.severity || 'minor';
    if (severityModalValue === currentSev) {
      setSeverityModalError('Select a new severity level different from the current one.');
      return;
    }
    if (!reason) {
      setSeverityModalError('Please provide a reason for the severity change.');
      return;
    }
    if (reason.length > 2000) {
      setSeverityModalError('Reason must be at most 2000 characters.');
      return;
    }
    setSeverityModalError('');
    try {
      await axios.put(`${API_ENDPOINTS.COMPLAINTS.LIST}/${complaintId}`, {
        severity: severityModalValue,
        severity_change_reason: reason,
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      await fetchComplaint(false);
      if (onUpdate) onUpdate();
      closeSeverityChangeModal();
      toast.success(`Severity changed to ${severityModalValue.toUpperCase()}`);
    } catch (error) {
      console.error('Error updating severity:', error);
      let errorMessage = 'Failed to update severity. Please try again.';

      if (error.response?.status === 403) {
        errorMessage = 'You are not allowed to change the severity of this complaint.';
        setActionBanner({
          type: 'error',
          title: '🔒 Not Authorized',
          message: errorMessage,
          severity: 'high',
          auditRelevant: true
        });
        closeSeverityChangeModal();
        return;
      }
      if (error.response?.status === 400) {
        const data = error.response.data || {};
        errorMessage = data.message || data.error || errorMessage;
        setSeverityModalError(errorMessage);
        return;
      }
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      setActionBanner({
        type: 'error',
        title: '❌ Severity Update Failed',
        message: errorMessage,
        severity: 'medium'
      });
      closeSeverityChangeModal();
    }
  };

  const handleStatusChange = async (newStatus, silentMode = false, extraFields = {}) => {
    try {
      const response = await axios.put(`${API_ENDPOINTS.COMPLAINTS.LIST}/${complaintId}`, {
        status: newStatus,
        ...extraFields,
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      await fetchComplaint(false); // Refresh data without showing error toast
      await fetchStatusTimeline(); // Refresh timeline after status change
      if (onUpdate) onUpdate();
      // Show toast notification if not in silent mode (device scan handles its own feedback)
      // Toast is appropriate: status changes are normal workflow steps
      // Medical device context: Status changes are tracked in audit history automatically
      if (!silentMode) {
        toast.success(`Status changed to ${newStatus.replace('_', ' ').toUpperCase()}`);
      } else {
        // In silent mode, log success for debugging
        //console.log('Silent mode status change succeeded');
      }
      return { success: true };
    } catch (error) {
      console.error('Error updating status:', error);
      if (!silentMode) {
        let errorMessage = 'Failed to update status. Please try again.';

        // Check for specific error types
        if (error.response?.status === 403) {
          // Authorization error: User lacks permission to change status
          // Medical device context: Access control for status changes is critical
          // Status changes determine workflow visibility and device service authorization
          errorMessage = 'You are not allowed to change the status of this complaint.';
          // Use banner for authorization errors (must be visible and not missed)
          setActionBanner({
            type: 'error',
            title: '🔒 Not Authorized',
            message: errorMessage,
            severity: 'high',
            auditRelevant: true
          });
          return { success: false, error: errorMessage };
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.message) {
          errorMessage = error.message;
        }

        // Generic status change errors: Use banner to ensure visibility
        // Medical device context: Status updates must not be missed or forgotten
        setActionBanner({
          type: 'error',
          title: '❌ Status Update Failed',
          message: errorMessage,
          severity: 'high'  // High severity because workflow is blocked
        });
      } else {
        // Even in silent mode, log the error for device scan operations
        console.error('Silent mode status change failed:', error.response?.data || error.message);

        // For device scan operations, we should still show authorization errors
        // Medical device context: Authorization failures during workflow are critical
        if (error.response?.status === 403) {
          const errorMsg = error.response?.data?.error || 'You are not allowed to change the status of this complaint.';
          // Use setScanError instead of setAuthError to keep all device scan feedback consistent
          // This ensures the error is displayed in the context of the scan workflow
        }
      }
      return { success: false, error: error.response?.data?.error || error.message };
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    setIsSubmittingNote(true);
    try {
      await axios.post(`${API_ENDPOINTS.COMPLAINTS.LIST}/${complaintId}/notes`, {
        note_text: newNote
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setNewNote('');
      await fetchComplaint(false); // Refresh data without showing error toast
      setIsSubmittingNote(false);
      // Success feedback: toast is appropriate for routine note posting
      // Medical device context: Note posting is a standard communication activity, not exceptional
      // Toast shows briefly and auto-dismisses, consistent with non-critical feedback
    } catch (error) {
      console.error('Error adding note:', error);
      // Note posting errors: toast is sufficient (non-critical workflow step)
      // Medical device context: Notes are supportive documentation, not core complaint data
      toast.error('Failed to add note');
      setIsSubmittingNote(false);
    }
  };

  const finalizeKeepOpenClose = async () => {
    setShowKeepOpenConfirm(false);
    setShowCloseForm(false);
    setCloseDeviceScanned(false);
    setComplaintDocsRefreshKey((key) => key + 1);
    await fetchComplaint(false);
  };

  const handleRequestKeepOpen = () => {
    if (pastBaselineDocumentCount > 0) {
      setShowKeepOpenConfirm(true);
      return;
    }
    finalizeKeepOpenClose();
  };

  const handleCloseComplaint = async (e) => {
    e.preventDefault();

    if (isTechnician && closeDeviceScanned && pastBaselineDocumentCount < 1) {
      toast.error('Upload at least one complaint report before closing.');
      return;
    }

    try {
      await axios.post(`${API_ENDPOINTS.COMPLAINTS.LIST}/${complaintId}/close`, {
        ...closeFormData,
        hours_spent: parseFloat(closeFormData.hours_spent) || 0
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      await fetchComplaint(false); // Refresh data without showing error toast
      await fetchStatusTimeline(); // Refresh timeline after closing complaint
      if (onUpdate) onUpdate();
      setShowCloseForm(false);
      setComplaintDocsRefreshKey((key) => key + 1);
      // Terminal StatusNotice + status badge already confirm closure — skip transient success banner
    } catch (error) {
      console.error('Error closing complaint:', error);
      let errorMessage = 'Failed to close complaint. Please try again.';

      // Check for specific error types
      if (error.response?.status === 403) {
        // Authorization error: User lacks permission to close complaint
        // Medical device context: Closure authority is critical - access control violation
        errorMessage = 'You are not authorized to close this complaint. Please check with your manager.';
        // Close the form modal before showing error banner
        setShowCloseForm(false);
        // Use banner for authorization errors (must be visible and not missed)
        setActionBanner({
          type: 'error',
          title: '🔒 Not Authorized',
          message: errorMessage,
          severity: 'high',
          auditRelevant: true
        });
        return;
      } else if (error.response?.status === 400 && error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Generic close errors: Use banner to ensure visibility
      // Medical device context: Closure failures block device release
      // Close the form modal before showing error banner
      setShowCloseForm(false);
      setActionBanner({
        type: 'error',
        title: '❌ Close Failed',
        message: errorMessage,
        severity: 'high'  // High severity because workflow is blocked
      });
    }
  };

  const handleCancelComplaint = async (e) => {
    e.preventDefault();

    if (!cancelReason.trim()) {
      // Validation error: User didn't provide required cancellation reason
      // Use banner because this is blocking workflow progression
      setActionBanner({
        type: 'error',
        title: '⚠️ Missing Information',
        message: 'Please provide a reason for cancellation',
        severity: 'medium'
      });
      return;
    }

    try {
      await axios.put(`${API_ENDPOINTS.COMPLAINTS.LIST}/${complaintId}`, {
        status: 'cancelled',
        cancellation_reason: cancelReason
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      await fetchComplaint(false); // Refresh data without showing error toast
      await fetchStatusTimeline(); // Refresh timeline after cancelling complaint
      if (onUpdate) onUpdate();
      setShowCancelForm(false);
      // Terminal StatusNotice + status badge already confirm cancellation — skip transient success banner
    } catch (error) {
      console.error('Error cancelling complaint:', error);
      let errorMessage = 'Failed to cancel complaint. Please try again.';

      // Check for specific error types
      if (error.response?.status === 403) {
        // Authorization error: User lacks permission to cancel complaint
        // Medical device context: Cancellation is workflow-critical - access control violation
        errorMessage = 'You are not authorized to cancel this complaint. Please check with your manager.';
        // Close the form modal before showing error banner
        setShowCancelForm(false);
        // Use banner for authorization errors (must be visible and not missed)
        setActionBanner({
          type: 'error',
          title: '🔒 Not Authorized',
          message: errorMessage,
          severity: 'high',
          auditRelevant: true
        });
        return;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Generic cancellation errors: Use banner to ensure visibility
      // Medical device context: Cancellation failures block workflow completion
      // Close the form modal before showing error banner
      setShowCancelForm(false);
      setActionBanner({
        type: 'error',
        title: 'Cancellation Failed',
        message: errorMessage,
        severity: 'high'  // High severity because workflow is blocked
      });
    }
  };

  const handleAcknowledge = async () => {
    setIsAcknowledging(true);
    try {
      const result = await handleStatusChange('acknowledged', true); // silentMode=true to suppress duplicate toast
      setIsAcknowledging(false);

      if (result && result.success) {
        // Acknowledge is a routine workflow step - use toast
        // Medical device context: Acknowledgment is a standard operation confirming technician receipt
        toast.success('Complaint acknowledged successfully');
      }
    } catch (error) {
      console.error('Error acknowledging complaint:', error);
      setIsAcknowledging(false);
      toast.error('Failed to acknowledge complaint');
    }
  };

  const completeInProgressTransition = async (deviceIdentifier, impactsAvailability) => {
    const extraFields = { scanned_device_id: deviceIdentifier };
    if (impactsAvailability !== undefined) {
      extraFields.impacts_availability = impactsAvailability;
    }
    const statusChangeResult = await handleStatusChange('in_progress', true, extraFields);

    if (statusChangeResult && statusChangeResult.success) {
      toast.success('Device verified and work started — complaint is now In Progress');
      setScannedDeviceId(null);
      return true;
    }

    console.error('Device scan status change failed:', statusChangeResult?.error);
    setScanError({
      type: 'status_transition_failed',
      title: 'Workflow Error',
      message: 'Device verified but unable to start work. You may not have permission to progress this complaint. Contact your manager if the issue persists.',
      severity: 'high',
      auditRelevant: true,
    });
    return false;
  };

  const closeStartWorkModal = () => {
    setShowStartWorkModal(false);
    setStartWorkDeviceScanned(false);
    setShowStartWorkDeviceScan(false);
    setStartWorkImpactChoice(null);
    setIsStartingWork(false);
    setScanError(null);
    setScannedDeviceId(null);
  };

  const handleStartWorkConfirm = async () => {
    if (startWorkImpactChoice === null) return;
    const deviceId = scannedDeviceId;
    if (!deviceId) return;

    setIsStartingWork(true);
    try {
      const ok = await completeInProgressTransition(deviceId, startWorkImpactChoice);
      if (ok) {
        closeStartWorkModal();
      } else {
        setIsStartingWork(false);
      }
    } catch (error) {
      console.error('Error starting work:', error);
      setIsStartingWork(false);
    }
  };

  const openEscalateForm = () => {
    setEscalationForm({
      vendor_name: '',
      contact_person: '',
      email: '',
      phone: '',
      issue_summary: '',
      technician_findings: '',
    });
    setEscalationFormError('');
    setShowEscalateForm(true);
  };

  const closeEscalateForm = () => {
    setShowEscalateForm(false);
    setEscalationFormError('');
    setIsEscalating(false);
  };

  const handleEscalateSubmit = async () => {
    const vendorName = (escalationForm.vendor_name || '').trim();
    const email = (escalationForm.email || '').trim();
    const issueSummary = (escalationForm.issue_summary || '').trim();
    const technicianFindings = (escalationForm.technician_findings || '').trim();
    const phoneDigits = (escalationForm.phone || '').replace(/\D/g, '');

    if (!vendorName || !email || !issueSummary || !technicianFindings) {
      setEscalationFormError('Vendor name, email, issue summary, and technician findings are required.');
      return;
    }

    const emailValidation = validateEmailAddress(email);
    if (!emailValidation.isValid) {
      setEscalationFormError(emailValidation.error || 'Please enter a valid vendor email address.');
      return;
    }

    if (phoneDigits) {
      const phoneValidation = validateIndianPhoneNumber(escalationForm.phone);
      if (!phoneValidation.isValid) {
        setEscalationFormError(phoneValidation.error || 'Please enter a valid 10-digit phone number.');
        return;
      }
    }

    setIsEscalating(true);
    setEscalationFormError('');
    try {
      const result = await handleStatusChange('escalated_to_vendor', true, {
        escalation: {
          vendor_name: vendorName,
          contact_person: (escalationForm.contact_person || '').trim(),
          email,
          phone: phoneDigits,
          issue_summary: issueSummary,
          technician_findings: technicianFindings,
        },
      });
      if (result?.success) {
        closeEscalateForm();
        toast.success('Complaint escalated to vendor');
      } else {
        setEscalationFormError(result?.error || 'Failed to escalate complaint to vendor.');
      }
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Failed to escalate complaint to vendor.';
      setEscalationFormError(message);
    } finally {
      setIsEscalating(false);
    }
  };

  const escalationEmailValidation = escalationForm.email
    ? validateEmailAddress(escalationForm.email)
    : { isValid: true, error: null };
  const escalationPhoneValidation = escalationForm.phone
    ? validateIndianPhoneNumber(escalationForm.phone)
    : { isValid: true, error: null };
  const hasInvalidEscalationContact = !escalationEmailValidation.isValid
    || !escalationPhoneValidation.isValid;

  const handleResumeWorkConfirm = async () => {
    setIsResuming(true);
    try {
      const result = await handleStatusChange('in_progress', true);
      if (result?.success) {
        setShowResumeConfirm(false);
        toast.success('Work resumed — complaint is back In Progress');
      }
    } finally {
      setIsResuming(false);
    }
  };

  const formatComplaintStatusLabel = (status) => {
    if (!status) return '';
    if (status === 'escalated_to_vendor') return 'ESCALATED TO VENDOR';
    return status.replace(/_/g, ' ').toUpperCase();
  };

  const getStatusDisplayPrefix = (status) => {
    if (status === 'closed') return '✓ ';
    if (status === 'cancelled') return '✗ ';
    if (status === 'escalated_to_vendor') return '🚩 ';
    return '';
  };

  const handleDeviceScan = async (scannedCode, context = 'start_work') => {
    // context: 'start_work' (transition to in_progress), 'close', 'cancel'
    // Technician-only feature: Device validation required for close/cancel operations
    setIsScanning(true);
    setScanError(null);

    // Helper to close the appropriate scanner based on context
    const closeScanner = () => {
      if (context === 'close') {
        setShowCloseDeviceScan(false);
      } else if (context === 'cancel') {
        setShowCancelDeviceScan(false);
      } else if (context === 'start_work') {
        setShowStartWorkDeviceScan(false);
      }
    };

    try {
      let deviceIdentifier = null;

      // Check if scanned code is a full URL (for QR codes with base64 tokens)
      if (scannedCode.includes('/device-scan')) {
        try {
          const url = new URL(scannedCode);
          // Extract token from URL path - it's the last segment after /device-scan
          // Format: https://yourdomain.com/device-scan?token={token}
          let tokenParam = url.searchParams.get('token');

          if (tokenParam) {
            // CRITICAL FIX: Backend handles base64 decoding, not frontend
            // Frontend should call the QR endpoint which decodes the token server-side
            // This prevents cryptographic manipulation and ensures proper decoding
            try {
              // SECURITY FIX: /api/devices/qr/{token} endpoint does NOT require authentication
              // because it validates organization/device ownership via base64-encoded token only
              // Token format: base64(org_id|device_identifier)
              const qrDeviceResponse = await axios.get(
                API_ENDPOINTS.DEVICES.QR_DEVICE(tokenParam)
                // No auth header needed - token provides all necessary context and validation
              );

              // Extract device identifier from the decoded response
              if (qrDeviceResponse.data && qrDeviceResponse.data.device_identifier) {
                deviceIdentifier = qrDeviceResponse.data.device_identifier;
              } else {
                console.error('Invalid response from QR device endpoint:', qrDeviceResponse.data);
                setScanError({
                  type: 'parse_error',
                  title: 'Invalid QR Code',
                  message: 'Failed to parse the scanned QR code. Please verify the code is not damaged or expired.',
                  severity: 'medium',
                });
                closeScanner();
                setIsScanning(false);
                return;
              }
            } catch (decryptError) {
              console.error('Error decrypting QR token via backend:', decryptError);

              // Check if it's an authentication or network error
              if (decryptError.response?.status === 401 || decryptError.response?.status === 403) {
                setScanError({
                  type: 'authorization',
                  title: '🔒 Not Authorized',
                  message: 'You do not have permission to access this QR code.',
                  severity: 'high',
                  auditRelevant: true,
                });
              } else {
                setScanError({
                  type: 'parse_error',
                  title: 'Invalid QR Code',
                  message: decryptError.response?.data?.message || 'Failed to parse the scanned QR code. Please verify the code is not damaged or expired.',
                  severity: 'medium',
                });
              }
              closeScanner();
              setIsScanning(false);
              return;
            }
          }
        } catch (urlError) {
          console.error('Error parsing scanned URL:', urlError);
          // QR code parse errors are technical issues - show banner for visibility
          // Medical device context: Corrupted QR codes may indicate compliance/audit concerns
          setScanError({
            type: 'parse_error',
            title: 'Invalid QR Code',
            message: 'Failed to parse the scanned QR code. Please verify the code is not damaged or expired.',
            severity: 'medium',
            // User action: Try scanning again or use manual device lookup
          });
          closeScanner();
          setIsScanning(false);
          return;
        }
      }

      if (deviceIdentifier) {
        // Store the scanned device ID for validation
        setScannedDeviceId(deviceIdentifier);

        // Verify scanned device with backend
        try {
          const verifyResponse = await axios.post(
            `${API_ENDPOINTS.COMPLAINTS.LIST}/${complaintId}/verify-device-scan`,
            { scanned_device_id: deviceIdentifier },
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );

          // Device matches - take action based on context
          if (context === 'close') {
            // CLOSE WORKFLOW: Device verified for close operation
            // Medical device context: Physical presence validation complete for technician
            setCloseDeviceScanned(true);
            setShowCloseDeviceScan(false);
            // Keep scannedDeviceId displayed in the verified section until form closes
          } else if (context === 'cancel') {
            // CANCEL WORKFLOW: Device verified for cancel operation
            // Medical device context: Physical presence validation complete for technician
            setCancelDeviceScanned(true);
            setShowCancelDeviceScan(false);
            // Keep scannedDeviceId displayed in the verified section until form closes
          } else if (context === 'start_work') {
            setStartWorkDeviceScanned(true);
            setShowStartWorkDeviceScan(false);
            setIsScanning(false);
            return;
          }
        } catch (verifyError) {
          // Backend verification failed
          console.error('Device verification failed:', verifyError);

          const errorData = verifyError.response?.data;
          const statusCode = verifyError.response?.status;

          // Handle different error types - CRITICAL: Each must be distinct for compliance
          // Medical device context: Error categorization is essential for audit trails
          if (statusCode === 403) {
            // AUTHORIZATION ERROR: User is not authorized for this specific complaint
            // Medical device context: This is an ACCESS CONTROL EVENT - must be logged separately
            // This prevents unauthorized personnel from accessing device data
            const assignedTo = errorData?.assigned_to || 'another Technician';
            setScanError({
              type: 'authorization',
              title: '🔒 Not Authorized',
              message: `This complaint is assigned to ${assignedTo}. Contact your manager to request reassignment.`,
              severity: 'high',
              auditRelevant: true,  // Flag for compliance: Access control violation attempt
              // User action: Request manager reassignment or switch to correct complaint
            });
          }
          else if (errorData?.error === 'Device verification failed') {
            // DEVICE MISMATCH: The physical device doesn't match the complaint
            // Medical device context: This is a critical SAFETY concern
            // Prevents wrong device from being serviced under wrong complaint history
            setScanError({
              type: 'device_mismatch',
              title: 'Device Mismatch',
              message: `You scanned: ${errorData.scanned} | This complaint is for: ${errorData.expected} | Please verify you have the correct device.`,
              severity: 'high',  // Critical for patient safety
              scanned: errorData.scanned,
              expected: errorData.expected,
              // User action: Verify physical device against complaint record - don't proceed
            });
          }
          else {
            // GENERIC VERIFICATION ERROR: Backend system/database failure
            // Medical device context: Technical issue - device couldn't be verified
            // May indicate database connectivity, API issues, or data integrity concerns
            setScanError({
              type: 'verification_failed',
              title: 'Verification Failed',
              message: errorData?.message || errorData?.error || 'Device verification failed. Please try again or contact support.',
              severity: 'medium',
              // User action: Retry scan or contact IT support for system issues
            });
          }

          closeScanner();
          setScannedDeviceId(null);
        }
      } else {
          setScannedDeviceId(null);

          // Invalid QR code with no deviceIdentifier extracted
          // Close scanner and show error banner for visibility
          setScanError({
            type: 'parse_error',
            title: 'Invalid QR Code',
            message: 'The scanned code is invalid. Please verify the QR code and try again.',
            severity: 'medium',
          });
          closeScanner();
      }
    } catch (error) {
      console.error('Error during device scan:', error);
      // Generic unexpected errors during scan processing
      setScanError({
        type: 'scan_error',
        title: 'Scan Error',
        message: 'Failed to process the device scan. Please try again.',
        severity: 'medium'
      });
      closeScanner();
      setScannedDeviceId(null);
    } finally {
      setIsScanning(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const calculateDaysDifference = (startDate) => {
    const daysSince = getDaysSinceNumeric(startDate);
    // Return "Today" for same-day events (0 days), otherwise return formatted text
    return daysSince === 0 ? 'Today' : `${daysSince} day${daysSince !== 1 ? 's' : ''}`;
  };

  const getDaysOpenStatus = (daysSince) => {
    // daysSince is already a number from getDaysSinceNumeric
    if (daysSince === 0) {
      return { text: 'Today', color: '#27ae60', severity: 'good' };
    } else if (daysSince <= 1) {
      return { text: `${daysSince}d`, color: '#27ae60', severity: 'good' };
    } else if (daysSince <= 3) {
      return { text: `${daysSince}d`, color: '#f39c12', severity: 'warning' };
    }
    return { text: `${daysSince}d`, color: '#e74c3c', severity: 'critical' };
  };

  const getWarrantyStatus = (dev) => {
    if (!dev || !dev.warranty_expiry) return 'Not set';
    const expiryDate = new Date(dev.warranty_expiry);
    const today = new Date();
    if (expiryDate < today) {
      const daysExpired = Math.floor((today - expiryDate) / (1000 * 60 * 60 * 24));
      return `Expired (${daysExpired} days ago)`;
    }
    const daysLeft = Math.floor((expiryDate - today) / (1000 * 60 * 60 * 24));
    return `Active (${daysLeft} days remaining)`;
  };

  const getMaintenanceStatus = (dev) => {
    if (!dev) return 'Not scheduled';
    const nextDate = dev.ppm_next_due_date;
    if (!nextDate) return 'Not scheduled';
    const dueDate = new Date(nextDate);
    const today = new Date();
    if (dueDate < today) {
      const daysOverdue = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
      return `Overdue (${daysOverdue} days)`;
    }
    const daysLeft = Math.floor((dueDate - today) / (1000 * 60 * 60 * 24));
    return `Due in ${daysLeft} days`;
  };

  const overlayClassName = isFullPage
    ? 'complaint-view-fullpage'
    : `modal-overlay${readOnlyReviewMode ? ' complaint-view-overlay-elevated' : ''}`;
  const overlayStyle =
    !isFullPage && readOnlyReviewMode ? { zIndex: 10200 } : undefined;

  if (loading) {
    const content = (
      <div className={overlayClassName} style={overlayStyle}>
        <div className="complaint-view-modal">
          <div className="loading">Loading complaint details...</div>
        </div>
      </div>
    );

    return isFullPage ? (
      <div className="complaint-view-modal">
        <div className="loading">Loading complaint details...</div>
      </div>
    ) : ReactDOM.createPortal(content, document.body);
  }

  if (!complaint) {
    return null;
  }

  const detailsText = (complaint.problem_details || '').trim();
  const detailsPreviewLimit = 180;
  const detailsNeedsClamp = detailsText.length > detailsPreviewLimit;
  const visibleDetails = detailsExpanded || !detailsNeedsClamp
    ? detailsText
    : `${detailsText.slice(0, detailsPreviewLimit).trimEnd()}…`;

  const resolutionText = (complaint.closing_remarks || '').trim();
  const resolutionPreviewLimit = 180;
  const resolutionNeedsClamp = resolutionText.length > resolutionPreviewLimit;
  const visibleResolution = resolutionExpanded || !resolutionNeedsClamp
    ? resolutionText
    : `${resolutionText.slice(0, resolutionPreviewLimit).trimEnd()}…`;

  const mainContent = (
    <div className={overlayClassName} style={overlayStyle}>
      <div className={isFullPage ? "complaint-view-fullpage-content" : "complaint-view-modal"}>
        <div className="modal-header">
          <div className="title-with-button">
            <h3>📋 Complaint Details</h3>
            <button
              type="button"
              onClick={() => {
                fetchComplaint();
                fetchStatusTimeline();
                fetchAllUsers();
                setComplaintDocsRefreshKey((key) => key + 1);
              }}
              className="btn-refresh"
              title="Refresh complaint"
            >
              🔄
            </button>
          </div>
          {isFullPage ? (
            <button className="action-btn action-secondary" onClick={onClose}>
              <span className="action-icon">←</span>
              <span className="action-text">{user && user.role === 'technician' ? 'Back to Dashboard' : 'Back to List'}</span>
            </button>
          ) : (
            <ModalCloseButton onClick={onClose} title="Close" />
          )}
        </div>

        {viewOnlyMessage && (
          <StatusNotice variant="warning" icon="🔒" plainIcon>
            {viewOnlyMessage}
          </StatusNotice>
        )}

        {isTerminalComplaint && (
          <StatusNotice
            variant={complaint.status === 'closed' ? 'success' : 'warning'}
            icon={complaint.status === 'closed' ? '🔒' : '🚫'}
            plainIcon
            title={
              complaint.status === 'closed' ? 'Complaint closed' : 'Complaint cancelled'
            }
          >
            This complaint is {complaint.status} and cannot be edited.
          </StatusNotice>
        )}

        {/* Authorization Error Banner */}
        {authError && (
          <StatusNotice
            variant="danger"
            icon={authError.type === 'authorization' ? '🔒' : undefined}
            plainIcon={authError.type === 'authorization'}
            title={authError.title}
            onDismiss={() => setAuthError(null)}
          >
            {authError.message}
          </StatusNotice>
        )}

        {/* Device Scan Error Banner */}
        {scanError && (
          <StatusNotice
            variant="danger"
            icon={
              scanError.type === 'authorization'
                ? '🔒'
                : scanError.type === 'mismatch'
                  ? '🔄'
                  : '❌'
            }
            plainIcon
            title={scanError.title}
            onDismiss={() => setScanError(null)}
          >
            {scanError.message}
          </StatusNotice>
        )}

        {/* Action Banner for blocking errors (auth / workflow failures) */}
        {actionBanner && (
          <StatusNotice
            variant="danger"
            plainIcon
            title={actionBanner.title}
            onDismiss={() => setActionBanner(null)}
          >
            {actionBanner.message}
          </StatusNotice>
        )}

        <div className="complaint-layout">
          {/* LEFT COLUMN: Main Content */}
          <div className="complaint-main">
            {/* Complaint Title with ID - Refined */}
            <div className="complaint-header-card">
              <div className="complaint-id-display">
                <ComplaintIDBadge complaintId={complaint.complaint_identifier} size="small" />
                <DeviceIDBadge deviceId={complaint.device_identifier} size="small" />
              </div>
              <div className="complaint-title-row">
                <h2 className="complaint-main-title">{complaint.title}</h2>
                {complaint.status && (
                  <StatusBadge status={complaint.status} size="small" />
                )}
              </div>
              <div className="complaint-category-meta">
                <span className="complaint-category-meta-label">Category</span>
                <span className="complaint-category-meta-value">
                  {complaint.category_label || '—'}
                </span>
              </div>
              {filedByYouVariant && (
                <p className="complaint-filed-by-you-note" role="note">
                  {filedByYouVariant === 'technician-blocked' && (
                    <>You filed this · assigned to {getAssignedEmployeeName() || 'another technician'} · only they can acknowledge or start work</>
                  )}
                  {filedByYouVariant === 'technician-unassigned' && (
                    <>You filed this · awaiting technician assignment</>
                  )}
                  {filedByYouVariant === 'tracking' && (
                    <>
                      You filed this
                      {complaint.assigned_employee_id
                        ? ` · assigned to ${getAssignedEmployeeName() || 'a technician'}`
                        : ' · not yet assigned'}
                    </>
                  )}
                </p>
              )}
            </div>

            {/* Details — always shown; empty state when reporter left it blank */}
            <div className="complaint-problem-card">
              <h4 className="problem-section-title">Details</h4>
              {detailsText ? (
                <>
                  <p className={`problem-text-prominent${detailsExpanded ? ' is-expanded' : ''}`}>
                    {visibleDetails}
                  </p>
                  {detailsNeedsClamp && (
                    <button
                      type="button"
                      className="problem-details-toggle"
                      onClick={() => setDetailsExpanded((prev) => !prev)}
                    >
                      {detailsExpanded ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </>
              ) : (
                <p className="problem-text-empty">No details provided by the reporter.</p>
              )}
            </div>

            {/* Timeline Card */}
            <div className="complaint-timeline-card">
              <button className="timeline-toggle-btn" onClick={() => toggleSection('timelineMetrics')}>
                <span className="toggle-icon">{expandedSections.timelineMetrics ? '▼' : '▶'}</span>
                <h4 className="timeline-section-title">Timeline & Metrics</h4>
              </button>
              {expandedSections.timelineMetrics && (
                <>
                  <AnalyticsContextBanner
                    variant="window"
                    compact
                    className="complaint-timeline-window-banner"
                    title={
                      deviceReliabilityMetrics?.observation_window?.label
                      || deviceComplaintHistory?.observation_window?.label
                      || 'Last 12 months'
                    }
                  >
                    Historical rates use a rolling 12-month window. Live active outage indicators are shown separately.
                  </AnalyticsContextBanner>
                  <p className="timeline-metrics-row-label">This incident</p>
                  <div className="timeline-grid timeline-grid-3col">
                    <div className="timeline-item">
                      <div className="timeline-icon">📅</div>
                      <div className="timeline-content">
                        <div className="timeline-label">Breakdown Since</div>
                        <div className="timeline-value">{formatDateShort(complaint.breakdown_since)}</div>
                        <div className="timeline-subtext">
                          {calculateDaysDifference(complaint.breakdown_since)}
                        </div>
                      </div>
                    </div>
                    <div className="timeline-item">
                      <div className="timeline-icon">⏰</div>
                      <div className="timeline-content">
                        <div className="timeline-label">Reported On</div>
                        <div className="timeline-value">{formatDateShort(complaint.created_at)}</div>
                        <div className="timeline-subtext">
                          {calculateDaysDifference(complaint.created_at)}
                        </div>
                      </div>
                    </div>
                    <div className="timeline-item">
                      <div className="timeline-icon">📊</div>
                      <div className="timeline-content">
                        <div className="timeline-label">Current Status</div>
                        <div className="timeline-value">
                          {getStatusDisplayPrefix(complaint.status)}
                          {formatComplaintStatusLabel(complaint.status)}
                        </div>
                        <div className="timeline-subtext">
                          {getDaysSinceNumeric(complaint.updated_at)}d in status
                        </div>
                      </div>
                    </div>
                    <div className="timeline-item">
                      <div className="timeline-icon">🏥</div>
                      <div className="timeline-content">
                        <div className="timeline-label">Availability Impact</div>
                        <div className="timeline-value">{getAvailabilityImpactLabel(complaint)}</div>
                      </div>
                    </div>
                    <div className="timeline-item">
                      <div className="timeline-icon">⏳</div>
                      <div className="timeline-content">
                        <div className="timeline-label">Incident Downtime (live)</div>
                        <div className="timeline-value">{formatReliabilityHours(computeIncidentDowntimeHours(complaint))}</div>
                      </div>
                    </div>
                  </div>

                  {/* Device reliability context */}
                  {(deviceReliabilityMetrics || deviceComplaintHistory) && (
                    <>
                      <p className="timeline-metrics-row-label" style={{ marginTop: '1.25rem' }}>Device (12 mo + live)</p>
                      <div className="timeline-grid timeline-grid-3col" style={{ marginTop: '0.75rem', borderTop: '1px solid #e9ecef', paddingTop: '1.5rem' }}>
{(() => {
                        const apiHealth = deviceReliabilityMetrics?.health_status || deviceComplaintHistory?.health_status;
                        const healthColor = getDeviceHealthColor(apiHealth);
                        return (
                          <div className="context-card" style={{ borderLeftColor: healthColor }}>
                            <div className="context-card-icon" style={{ fontSize: '1.5rem' }}>
                              {apiHealth === 'healthy' ? '✅' : apiHealth === 'at_risk' ? '⚠️' : '🔴'}
                            </div>
                            <div className="context-card-content">
                              <div className="context-card-label">Device Health</div>
                              <div className="context-card-status" style={{ color: healthColor }}>
                                {getDeviceHealthLabel(apiHealth)}
                              </div>
                              {(deviceReliabilityMetrics?.health_reasons || deviceComplaintHistory?.health_reasons || []).slice(0, 2).map((reason, idx) => (
                                <div key={idx} className="context-detail-item">{reason}</div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      <div className="context-card">
                        <div className="context-card-icon">📈</div>
                        <div className="context-card-content">
                          <div className="context-card-label">Availability % (12 mo)</div>
                          <div className="context-card-status">
                            {deviceReliabilityMetrics?.availability_percentage != null
                              ? `${Number(deviceReliabilityMetrics.availability_percentage).toFixed(1)}%`
                              : '—'}
                          </div>
                        </div>
                      </div>

                      <div className="context-card">
                        <div className="context-card-icon">⏳</div>
                        <div className="context-card-content">
                          <div className="context-card-label">Current Downtime (live)</div>
                          <div className="context-card-status">
                            {formatReliabilityHours(
                              deviceReliabilityMetrics?.current_downtime_hours_live
                                ?? ((deviceReliabilityMetrics?.current_downtime_days || 0) * 24
                                  + (deviceReliabilityMetrics?.current_downtime_hours || 0))
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="context-card">
                        <div className="context-card-icon">⏱️</div>
                        <div className="context-card-content">
                          <div className="context-card-label">Avg Restore (12 mo)</div>
                          <div className="context-card-status">
                            {formatReliabilityHours(deviceReliabilityMetrics?.avg_restore_hours)}
                          </div>
                        </div>
                      </div>

                      <div className="context-card">
                        <div className="context-card-icon">🔁</div>
                        <div className="context-card-content">
                          <div className="context-card-label">MTBF (12 mo)</div>
                          <div className="context-card-status">{formatMtbfFromMetrics(deviceReliabilityMetrics)}</div>
                        </div>
                      </div>

                      <div className="context-card">
                        <div className="context-card-icon">🔧</div>
                        <div className="context-card-content">
                          <div className="context-card-label">Complaints (12 mo)</div>
                          <div className="context-card-stat">
                            <span className="context-card-number">
                              {deviceReliabilityMetrics?.total_complaints_12mo
                                ?? deviceComplaintHistory?.total_complaints
                                ?? 0}
                            </span>
                            <span className="context-card-text">total</span>
                          </div>
                          <div className="context-card-details">
                            <span className="context-detail-item">
                              {deviceReliabilityMetrics?.failures_count_12mo ?? '—'} impacting failures
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                      {onViewFullDeviceDetails && device && (
                        <button type="button" className="timeline-reliability-link" onClick={() => onViewFullDeviceDetails(device)}>
                          View full device reliability →
                        </button>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            {/* Evidence photos for this complaint */}
            <div className="complaint-documents-section">
              <h4 className="problem-section-title">
                Evidence Photos ({evidencePhotoCount})
              </h4>
              <p className="form-helper-text">
                Photos attached when this complaint was raised.
              </p>
              <DocumentManager
                key={`evidence-photos-${complaint.id}-${complaintDocsRefreshKey}`}
                complaintId={complaint.id}
                token={token}
                user={user}
                documentOptions={COMPLAINT_DOCUMENT_OPTIONS}
                allowedDocumentTypes={complaintPhotoDocumentTypes}
                listDocumentType={COMPLAINT_DOCUMENT_TYPE_PHOTO}
                permissions={{ canUpload: false, canPreview: true, canDelete: false }}
                emptyStateText="No evidence photos attached when this complaint was raised."
                onDocumentCountChange={setEvidencePhotoCount}
              />
            </div>

            {/* Closure reports for this complaint (view outside close modal) */}
            <div className="complaint-documents-section">
              <h4 className="problem-section-title">
                Complaint Reports ({complaintReportCount})
              </h4>
              <p className="form-helper-text">
                Closure reports linked to this complaint.
                {user?.role === 'technician'
                  ? ' You can delete reports you uploaded within the last 24 hours.'
                  : canDeleteComplaintReports
                    ? ' Authorized users can delete reports if needed.'
                    : ''}
              </p>
              <DocumentManager
                key={`complaint-reports-${complaint.id}-${complaintDocsRefreshKey}`}
                complaintId={complaint.id}
                token={token}
                user={user}
                documentOptions={COMPLAINT_DOCUMENT_OPTIONS}
                allowedDocumentTypes={complaintReportDocumentTypes}
                listDocumentType={COMPLAINT_DOCUMENT_TYPE_REPORT}
                permissions={{
                  canUpload: false,
                  canPreview: true,
                  canDelete: canDeleteComplaintReports,
                }}
                deleteOnlyOwn={user?.role === 'technician'}
                emptyStateText="No closure reports uploaded for this complaint yet."
                onDocumentCountChange={setComplaintReportCount}
              />
            </div>

            {/* Documents from other complaints on this device (collapsed by default; same table UX as above) */}
            <div className="complaint-documents-section device-complaint-history-docs">
              <button
                type="button"
                className="device-history-docs-toggle"
                onClick={() => setDeviceHistoryExpanded((open) => !open)}
              >
                <span className="toggle-icon">{deviceHistoryExpanded ? '▼' : '▶'}</span>
                <span>
                  Documents from other complaints on this device
                  {' '}
                  ({complaint.device_complaint_document_count ?? 0})
                </span>
              </button>
              {deviceHistoryExpanded && complaint.device_id && (
                <>
                  {/* Same helper → DocumentManager sibling layout as Complaint Reports */}
                  <p className="form-helper-text">
                    Shows files attached to different complaints for the same device (not this
                    complaint). Device manuals and PPM/calibration reports remain on the Device page.
                  </p>
                  <DocumentManager
                    key={`device-complaint-history-${complaint.device_id}-${complaint.id}-${complaintDocsRefreshKey}`}
                    deviceId={complaint.device_id}
                    excludeComplaintId={complaint.id}
                    token={token}
                    user={user}
                    documentOptions={COMPLAINT_DOCUMENT_OPTIONS}
                    permissions={{ canUpload: false, canPreview: true, canDelete: false }}
                    emptyStateText="No documents from other complaints on this device."
                    instanceId="device-complaint-history"
                  />
                </>
              )}
            </div>

            {/* Closing Information (if closed) - Compact Card */}
            {complaint.status === 'closed' && complaint.closing_remarks && (
              <div className="resolution-card">
                <div className="resolution-header">
                  <h4 className="resolution-title">✓ Resolution Details</h4>
                </div>
                <div className="resolution-content">
                  <div className="resolution-grid">
                    <div className="resolution-item">
                      <span className="resolution-label">Closed On</span>
                      <span className="resolution-value">{formatDateShort(complaint.closed_at)}</span>
                    </div>
                    <div className="resolution-item">
                      <span className="resolution-label">Spare Parts</span>
                      <span className={`resolution-badge ${complaint.spare_parts_required ? 'badge-yes' : 'badge-no'}`}>
                        {complaint.spare_parts_required ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                  <div className="resolution-remarks">
                    <span className="resolution-label">How it was resolved</span>
                    <p className={`resolution-text${resolutionExpanded ? ' is-expanded' : ''}`}>
                      {visibleResolution}
                    </p>
                    {resolutionNeedsClamp && (
                      <button
                        type="button"
                        className="resolution-text-toggle"
                        onClick={() => setResolutionExpanded((prev) => !prev)}
                      >
                        {resolutionExpanded ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Cancellation Information (if cancelled) - Compact Card */}
            {complaint.status === 'cancelled' && complaint.cancellation_reason && (
              <div className="cancellation-card">
                <div className="cancellation-header">
                  <h4 className="cancellation-title">✗ Cancellation Details</h4>
                </div>
                <div className="cancellation-content">
                  <div className="cancellation-date">
                    <span className="cancellation-label">Cancelled On</span>
                    <span className="cancellation-value">{formatDateShort(complaint.updated_at)}</span>
                  </div>
                  <div className="cancellation-reason-section">
                    <span className="cancellation-label">Reason for Cancellation</span>
                    <p className="cancellation-text">{complaint.cancellation_reason}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Notes Section */}
            <div className="notes-section">
              <div className="notes-header">
                <h4>Communication Log</h4>
                {complaint.notes && complaint.notes.length > 1 && (
                  <button
                    className="notes-toggle-btn"
                    onClick={() => setNotesExpanded(!notesExpanded)}
                    title={notesExpanded ? 'Collapse history' : 'View all notes'}
                  >
                    <span className="notes-toggle-icon">{notesExpanded ? '▼' : '▶'}</span>
                    <span className="notes-toggle-text">
                      {notesExpanded ? 'Hide' : 'Show'} all ({complaint.notes.length})
                    </span>
                  </button>
                )}
              </div>

              {canPostNotes && complaint.status !== 'closed' && complaint.status !== 'cancelled' && (
                <form onSubmit={handleAddNote} className="add-note-form">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note or update..."
                    rows="3"
                    disabled={isSubmittingNote}
                  />
                  <button
                    type="submit"
                    className="action-btn action-primary"
                    disabled={isSubmittingNote || !newNote.trim()}
                  >
                    <span className="action-icon">💬</span>
                    <span>{isSubmittingNote ? 'Posting...' : 'Post Note'}</span>
                  </button>
                </form>
              )}

              <div className="notes-list">
                {complaint.notes && complaint.notes.length > 0 ? (
                  <>
                    {/* Show latest note always */}
                    <div key={complaint.notes[complaint.notes.length - 1].id} className="note-item note-item-latest">
                      <div className="note-header">
                        <ComplaintNoteAuthorHeader
                          note={complaint.notes[complaint.notes.length - 1]}
                          getUserNameByEmployeeId={getUserNameByEmployeeId}
                        />
                        <div className="note-header-trailing">
                          <span className="note-time">{formatDate(complaint.notes[complaint.notes.length - 1].created_at)}</span>
                          <span className="note-badge">Latest</span>
                        </div>
                      </div>
                      <div className="note-content">{complaint.notes[complaint.notes.length - 1].note_text}</div>
                    </div>

                    {/* Show rest of notes when expanded */}
                    {notesExpanded && complaint.notes.length > 1 && (
                      <div className="notes-history">
                        {complaint.notes.slice(0, -1).reverse().map((note) => (
                          <div key={note.id} className="note-item">
                            <div className="note-header">
                              <ComplaintNoteAuthorHeader note={note} getUserNameByEmployeeId={getUserNameByEmployeeId} />
                              <div className="note-header-trailing">
                                <span className="note-time">{formatDate(note.created_at)}</span>
                              </div>
                            </div>
                            <div className="note-content">{note.note_text}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="empty-notes">
                    {canManageComplaints ? 'No notes yet. Add the first note above.' : 'No communication notes yet.'}
                  </div>
                )}
              </div>
            </div>

            {/* Status Timeline - Full History */}
            <div className="complaint-timeline-card">
              <button className="timeline-toggle-btn" onClick={() => setStatusTimelineExpanded(!statusTimelineExpanded)}>
                <span className="toggle-icon">{statusTimelineExpanded ? '▼' : '▶'}</span>
                <h4 className="timeline-section-title">Status Timeline</h4>
              </button>
              {statusTimelineExpanded && (
                <ComplaintTimeline
                  timeline={statusTimeline}
                  loading={timelineLoading}
                  error={timelineError}
                />
              )}
            </div>
          </div>

          {/* RIGHT COLUMN: Sticky Sidebar with Status Controls */}
          <div className="complaint-sidebar">
            {/* Severity Control - Technicians and Admins only */}
            {!isCustodian && (
              <div className="sidebar-section">
                <div className="sidebar-label">Severity</div>
                <div className="sidebar-display">
                  <SeverityBadge severity={complaint.severity} size="medium" />
                  {canEditSeverity && (
                    <button
                      type="button"
                      className="sidebar-edit-btn"
                      onClick={openSeverityChangeModal}
                      title="Change severity after diagnosis"
                    >
                      ✏️
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="sidebar-section">
              <div className="sidebar-label">Availability Impact</div>
              <div className="sidebar-display">
                <AvailabilityImpactBadge complaint={complaint} />
              </div>
            </div>

            {complaint.acknowledge_due_at && (
              <div className="sidebar-section complaint-tat-panel">
                <div className="sidebar-label">SLA / TAT</div>
                {getComplaintTatPolicyTooltip(complaint) && (
                  <div className="complaint-tat-policy-summary" aria-label="Applicable TAT policy">
                    {getComplaintTatPolicyTooltip(complaint).split('\n').map((line, i) => (
                      <span
                        key={line}
                        className={
                          i === 0 ? 'complaint-tat-policy-summary-title' : undefined
                        }
                      >
                        {line}
                      </span>
                    ))}
                  </div>
                )}
                {getComplaintTatDetailRows(complaint).map((row) => (
                  <div
                    key={row.key}
                    className={`tat-detail-row tat-${row.state} ${
                      row.slaBadge?.met === true
                        ? 'tat-sla-met'
                        : row.slaBadge?.met === false
                          ? 'tat-sla-missed'
                          : ''
                    }`}
                  >
                    <div className="tat-detail-title-row">
                      <span className="tat-detail-title">{row.title}</span>
                      <div className="tat-detail-badges">
                        {row.slaBadge && (
                          <span
                            className={`tat-sla-pill ${
                              row.slaBadge.met ? 'tat-sla-pill--met' : 'tat-sla-pill--missed'
                            }`}
                          >
                            {row.slaBadge.met ? '✓' : '⚠'} {row.slaBadge.text}
                          </span>
                        )}
                        {row.escalatedAt && (
                          <button
                            type="button"
                            className={`tat-escalation-badge${
                              expandedEscalationKey === row.key
                                ? ' tat-escalation-badge--open'
                                : ''
                            }`}
                            onClick={() =>
                              setExpandedEscalationKey((prev) =>
                                prev === row.key ? null : row.key,
                              )
                            }
                            aria-expanded={expandedEscalationKey === row.key}
                            aria-controls={`tat-escalation-${row.key}`}
                          >
                            Escalated
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="tat-detail-duration">{row.duration}</div>
                    {row.graceLine && (
                      <div className="tat-detail-grace">{row.graceLine}</div>
                    )}
                    {row.policyTarget && row.graceLine && (
                      <div className="tat-detail-policy-target">
                        Policy due: {row.policyTarget}
                      </div>
                    )}
                    <div className="tat-detail-target">
                      {row.targetHint ? `${row.targetHint}: ` : 'Due: '}
                      {row.target}
                    </div>
                    {row.completedAt && (
                      <div className="tat-detail-completed">Completed: {row.completedAt}</div>
                    )}
                    {row.policyMinutes != null && (
                      <div className="tat-detail-policy">
                        Target: {formatMinutesAsDuration(row.policyMinutes)}
                      </div>
                    )}
                    {row.escalatedAt && expandedEscalationKey === row.key && (
                      <div
                        id={`tat-escalation-${row.key}`}
                        className="tat-escalation-detail"
                        role="region"
                        aria-label="Escalation details"
                      >
                        <p className="tat-escalation-detail-line">
                          Breach escalated: {formatDueInstant(row.escalatedAt)}
                        </p>
                        <p className="tat-escalation-detail-line tat-escalation-detail-line--muted">
                          {TAT_ESCALATION_NOTIFY_LINE}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Status Control */}
            <div className="sidebar-section">
              <div className="sidebar-label">Status</div>
              <div className="sidebar-display">
                <span className={`status-badge-sidebar status-${complaint.status}`}>
                  {getStatusDisplayPrefix(complaint.status)}
                  {formatComplaintStatusLabel(complaint.status)}
                </span>
              </div>
              {complaint.status !== 'closed' && complaint.status !== 'cancelled' && (
                <>
                  <div className="sidebar-action-buttons">
                    {complaint.status === 'open' && canAcknowledge && (
                      <button
                        className="sidebar-btn sidebar-btn-info"
                        onClick={handleAcknowledge}
                        disabled={isAcknowledging}
                        title="Mark this complaint as acknowledged"
                      >
                        {isAcknowledging ? '⏳ Acknowledging...' : '✓ Acknowledge'}
                      </button>
                    )}
                    {complaint.status === 'acknowledged' && canScanDevice && (
                      <button
                        className="sidebar-btn sidebar-btn-primary"
                        onClick={() => setShowStartWorkModal(true)}
                        disabled={!complaint.assigned_employee_id}
                        title={!complaint.assigned_employee_id ? 'Assign to a Technician first' : 'Start work on this complaint'}
                      >
                        ▶ Start Work
                      </button>
                    )}
                    {canEscalateToVendor && (
                      <button
                        className="sidebar-btn sidebar-btn-warning"
                        onClick={openEscalateForm}
                        title="Escalate to vendor for external support"
                      >
                        Escalate to Vendor
                      </button>
                    )}
                    {canResumeVendorWork && (
                      <button
                        className="sidebar-btn sidebar-btn-primary"
                        onClick={() => setShowResumeConfirm(true)}
                        title="Vendor is onsite — resume work"
                      >
                        Resume Work
                      </button>
                    )}
                    {canCloseComplaint && (
                      <button className="sidebar-btn sidebar-btn-success" onClick={() => setShowCloseForm(true)}>
                        Close
                      </button>
                    )}
                    {canCancelComplaint && (
                      <button className="sidebar-btn sidebar-btn-danger" onClick={() => setShowCancelForm(true)}>
                        Cancel
                      </button>
                    )}
                  </div>
                  {complaint.status === 'open' && !complaint.assigned_employee_id && !isCustodian && (
                    <div className="status-info-message">
                      <span className="info-icon">ℹ️</span>
                      <span className="info-text">Assign to a Technician in the section below to acknowledge the complaint</span>
                    </div>
                  )}
                  {complaint.status === 'acknowledged' && complaint.assigned_employee_id && canScanDevice && (
                    <div className="status-info-message">
                      <span className="info-icon">▶</span>
                      <span className="info-text">Start work to scan the device, confirm availability impact, and move this ticket to In Progress</span>
                    </div>
                  )}
                  {complaint.status === 'escalated_to_vendor' && canResumeVendorWork && (
                    <div className="status-info-message">
                      <span className="info-icon">🚩</span>
                      <span className="info-text">Waiting on vendor. Resume work when the vendor is onsite, then close when complete.</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Assignment Control - Technicians, Department Managers, Central Help Desk, and Admins can see/edit */}
            {!isCustodian && !isCoordinator && (
              <div className="sidebar-section">
                <div className="sidebar-label">Assigned To</div>
                {isEditingAssignment ? (
                  <div className="sidebar-edit-group">
                    {loadingEngineers ? (
                      <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--dp-accent-primary)' }}>
                        <span>Loading Technicians...</span>
                      </div>
                    ) : technicians.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '1rem', color: '#e74c3c' }}>
                        <span>No Technicians assigned to {complaint.department} department</span>
                      </div>
                    ) : (
                      <>
                        <select
                          value={assignmentValue}
                          onChange={(e) => setAssignmentValue(e.target.value)}
                          className="sidebar-select assignment-dropdown"
                          autoFocus
                          disabled={loadingEngineers}
                        >
                          <option value="">-- Select Technician --</option>
                          {technicians.map((engineer) => (
                            <option key={engineer.id} value={engineer.employee_id}>
                              {engineer.full_name}
                            </option>
                          ))}
                        </select>
                      </>
                    )}
                    <div className="sidebar-button-group">
                      <button className="sidebar-btn sidebar-btn-save" onClick={handleSaveAssignment} disabled={loadingEngineers || technicians.length === 0}>Save</button>
                      <button className="sidebar-btn sidebar-btn-cancel" onClick={handleCancelAssignment}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="sidebar-display">
                    {complaint.assigned_employee_id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>
                        <span className="assigned-name-sidebar">
                          {getAssignedEmployeeName()}
                        </span>
                        <EmployeeIDBadge employeeId={complaint.assigned_employee_id} size="small" />
                      </div>
                    ) : (
                      <span className="unassigned-text">Unassigned</span>
                    )}
                    {canAssignComplaints && complaint.status !== 'closed' && complaint.status !== 'cancelled' && (
                      <button className="sidebar-edit-btn" onClick={() => setIsEditingAssignment(true)}>✏️</button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Reporter Info - Compact */}
            <div className="sidebar-section">
              <button className="sidebar-toggle-btn" onClick={() => toggleSection('reporterInfo')}>
                <span className="sidebar-toggle-icon">{expandedSections.reporterInfo ? '▼' : '▶'}</span>
                <span className="sidebar-label">👤 Reported By</span>
              </button>
              {expandedSections.reporterInfo && (
                <div className="sidebar-display" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.4rem', marginTop: '0.5rem' }}>
                  <span className="assigned-name-sidebar" style={{ fontSize: '0.9rem', fontWeight: '500' }}>
                    {complaint.reporter_full_name || complaint.reporter_employee_id || 'N/A'}
                  </span>
                  {complaint.reporter_email && (
                    <span style={{ fontSize: '0.8rem', color: 'var(--dp-accent-primary)', wordBreak: 'break-word' }}>
                      ✉️ {complaint.reporter_email}
                    </span>
                  )}
                  {complaint.reporter_contact_number && (
                    <span style={{ fontSize: '0.8rem', color: '#2c3e50', wordBreak: 'break-word' }}>
                      📞 {complaint.reporter_contact_number}
                    </span>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span
                      className={`reporter-verification-badge ${complaint.is_authenticated_reporter ? 'registered' : 'manual'}`}
                      title={complaint.is_authenticated_reporter ? 'Registered: Reporter matched to a system user account' : 'Manual: Reporter details were entered manually (not registered)'}
                    >
                      {complaint.is_authenticated_reporter ? 'Registered' : 'Manual'}
                    </span>
                    <button
                      type="button"
                      className="user-form-info-btn"
                      title={reporterBadgeHelpOpen ? 'Hide badge help' : 'What do these badges mean?'}
                      aria-expanded={reporterBadgeHelpOpen}
                      aria-controls="reporter-badge-help"
                      onClick={() => {
                        setReporterBadgeHelpOpen((o) => !o);
                      }}
                    >
                      <span className="user-form-info-icon" aria-hidden="true">ℹ️</span>
                      <span className="sr-only">Reporter badges help</span>
                    </button>
                  </div>
                  {reporterBadgeHelpOpen && (
                    <div id="reporter-badge-help" className="user-form-help-panel" role="note">
                      <div className="reporter-badge-legend-item">
                        <span className="reporter-verification-badge registered">Registered</span>
                        <div className="reporter-badge-legend-details">
                          Reporter matched to a system user account.
                        </div>
                      </div>
                      <div className="reporter-badge-legend-item">
                        <span className="reporter-verification-badge manual">Manual</span>
                        <div className="reporter-badge-legend-details">
                          Reporter details were entered manually (not registered in the system).
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Device Context - Condensed */}
            <div className="sidebar-section">
              <button className="sidebar-toggle-btn" onClick={() => toggleSection('deviceContext')}>
                <span className="sidebar-toggle-icon">{expandedSections.deviceContext ? '▼' : '▶'}</span>
                <span className="sidebar-label">Device Context</span>
              </button>
              {expandedSections.deviceContext && (
              <div className="device-context">
                <div className="device-context-item">
                  <span className="device-context-label">Device</span>
                  <span className="device-context-value">{complaint.device_name}</span>
                </div>
                <div className="device-context-item">
                  <span className="device-context-label">Device ID</span>
                  <DeviceIDBadge deviceId={complaint.device_identifier} size="small" />
                </div>
                {complaint.device_navigable === false && complaint.device_current_location_name && (
                  <div className="device-transferred-notice" role="status">
                    Device transferred to <strong>{complaint.device_current_location_name}</strong>
                  </div>
                )}
                {complaint.department && (
                  <div className="device-context-item">
                    <span className="device-context-label">Department</span>
                    <div className="department-badge-wrapper">
                      <span className="department-icon-sidebar">
                        {departments.find(d => d.name === complaint.department)?.icon || '🏢'}
                      </span>
                      <span className="department-badge-sidebar">
                        {complaint.department}
                      </span>
                    </div>
                  </div>
                )}
                {device && (
                  <>
                    <div className="device-context-divider"></div>
                    <div className="device-context-item">
                      <span className="device-context-label">Warranty</span>
                      <span className={`device-context-status ${getWarrantyStatus(device).includes('Expired') ? 'warning' : 'success'}`}>
                        {getWarrantyStatus(device)}
                      </span>
                    </div>
                    <div className="device-context-item">
                      <span className="device-context-label">Maintenance</span>
                      <span className={`device-context-status ${getMaintenanceStatus(device).includes('Overdue') ? 'critical' : 'info'}`}>
                        {getMaintenanceStatus(device)}
                      </span>
                    </div>
                    {device.service_contract_type && (
                      <div className="device-context-item">
                        <span className="device-context-label">Contract</span>
                        <div className="device-context-contract">
                          <span className="device-context-value">{device.service_contract_type}</span>
                          {getDeviceContractValidityText(device) && (
                            <span className="device-context-contract-validity">
                              {getDeviceContractValidityText(device)}
                            </span>
                          )}
                          <ContractCoverageBadge
                            status={getDeviceContractCoverageStatus(device)}
                            size="small"
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
                {device && onViewFullDeviceDetails && complaint.device_navigable !== false && (
                  <button
                    className="view-full-device-link-sidebar"
                    onClick={() => onViewFullDeviceDetails({id: complaint.device_id, serial_number: complaint.serial_number})}
                    title="View complete device details"
                  >
                    View Full Details →
                  </button>
                )}
              </div>
              )}
            </div>
          </div>
        </div>

        {showSeverityChangeModal && complaint && ReactDOM.createPortal(
          <div className="form-modal-overlay">
            <div
              className="form-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="severity-change-title"
            >
              <div className="form-modal-header">
                <h3 id="severity-change-title" className="form-modal-title">Change severity</h3>
                <ModalCloseButton
                  onClick={closeSeverityChangeModal}
                  title="Close without saving"
                />
              </div>
              <div className="form-modal-content">
                <p className="form-helper-text">
                  Adjust severity based on initial diagnosis. This is recorded on the complaint and
                  sent to the reporter and location managers.
                </p>
                {severityModalError && (
                  <div className="scan-error" style={{ marginBottom: '12px' }}>
                    <p className="error-message">{severityModalError}</p>
                  </div>
                )}
                <div className="form-section">
                  <label className="info-label" htmlFor="severity-modal-select">New severity</label>
                  <select
                    id="severity-modal-select"
                    className="sidebar-select"
                    style={{ width: '100%', marginTop: '8px' }}
                    value={severityModalValue}
                    onChange={(e) => setSeverityModalValue(e.target.value)}
                  >
                    <option value="minor">⚪ Minor</option>
                    <option value="major">🟠 Major</option>
                    <option value="critical">🔴 Critical</option>
                  </select>
                  <p className={`severity-desc severity-${severityModalValue || 'minor'}`}>
                    {getComplaintSeverityDescription(severityModalValue)}
                  </p>
                </div>
                <div className="form-section">
                  <label className="info-label" htmlFor="severity-change-reason">Reason for change (required)</label>
                  <textarea
                    id="severity-change-reason"
                    className="sidebar-select"
                    style={{ width: '100%', marginTop: '8px', minHeight: '100px', resize: 'vertical' }}
                    placeholder="Why is the severity being changed?"
                    maxLength={2000}
                    value={severityChangeReason}
                    onChange={(e) => setSeverityChangeReason(e.target.value)}
                  />
                </div>
                <div className="form-actions">
                  <button type="button" className="action-btn action-secondary" onClick={closeSeverityChangeModal}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="action-btn action-primary"
                    onClick={handleConfirmSeverityChange}
                    disabled={
                      severityModalValue === (complaint.severity || 'minor') ||
                      !(severityChangeReason || '').trim()
                    }
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Close Complaint Form Modal - Using ReactDOM.createPortal */}
        {/* CRITICAL: BarcodeScanner MUST be INSIDE the same portal as form-modal */}
        {/* This prevents z-index stacking context issues and ensures proper layering */}
        {showCloseForm && ReactDOM.createPortal(
          <>
            {/* Barcode Scanner - INSIDE the portal, rendered on top */}
            {isTechnician && showCloseDeviceScan && (
              <BarcodeScanner
                onScan={(code) => handleDeviceScan(code, 'close')}
                onClose={() => setShowCloseDeviceScan(false)}
                title="Verify Device"
                subtitle="Scan the device QR code to verify before closing this complaint."
                expectedDeviceId={complaint.device_identifier}
              />
            )}

            {/* Form Modal - Same portal as scanner */}
            <div className="form-modal-overlay">
              <div className="form-modal form-modal--workflow" onClick={(e) => e.stopPropagation()}>
                <div className="form-modal-header">
                  <h3 className="form-modal-title">✓ Close Complaint</h3>
                  <ModalCloseButton
                    onClick={handleRequestKeepOpen}
                    title="Keep complaint open"
                  />
                </div>

                <div className="form-modal-content">
                  {/* SCAN-FIRST MODE: Only show device info and scan section until device is verified (Technician only) */}
                  {isTechnician && !closeDeviceScanned ? (
                  <>
                    {/* Device Info Section */}
                    <div className="form-section device-info-section">
                      <h3 className="section-title">Device Information</h3>
                      <div className="device-info-display">
                        <div className="info-row">
                          <span className="info-label">Device ID:</span>
                          <span className="info-value">
                            <DeviceIDBadge deviceId={complaint.device_identifier || 'N/A'} size="small" />
                          </span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Device Name:</span>
                          <span className="info-value">{complaint.device_name || 'N/A'}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Complaint Action:</span>
                          <span className="info-value" style={{ color: '#27ae60', fontWeight: '600' }}>Close</span>
                        </div>
                      </div>
                    </div>

                    {/* Device Scan Section - Required First Step */}
                    <div className="form-section scan-section scan-required">
                      <h3 className="section-title">Scan Device to Continue</h3>
                      <p className="form-helper-text">Scan the device QR code to verify physical presence before closing the complaint.</p>
                      {scanError && (
                        <div className="scan-error">
                          <p className="error-title">{scanError.title}</p>
                          <p className="error-message">{scanError.message}</p>
                        </div>
                      )}
                      <button
                        type="button"
                        className="action-btn action-primary btn-scan-device"
                        onClick={() => setShowCloseDeviceScan(true)}
                        disabled={isScanning}
                      >
                        <span className="action-icon">📷</span>
                        <span className="action-text">Scan Device</span>
                      </button>
                    </div>

                    {/* Form Actions - Only Cancel available during scan phase */}
                    <div className="form-actions">
                      <button
                        type="button"
                        onClick={() => {
                          setShowCloseForm(false);
                          setCloseDeviceScanned(false);
                        }}
                        className="action-btn action-secondary"
                        disabled={isScanning}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* FORM MODE: Show full close form after device is scanned (or for non-technicians) */}

                    {/* Device Verified Status - Only show for technicians who scanned */}
                    {isTechnician && closeDeviceScanned && (
                      <DeviceVerifiedBanner scannedDeviceId={scannedDeviceId} />
                    )}

                    <div className="close-reason-info">
                      <p className="form-warning-enhanced">
                        This action will mark the complaint as closed and complete the device service. The record will be preserved for compliance and cannot be reopened.
                      </p>
                    </div>

                    <form onSubmit={handleCloseComplaint}>
                      <div className="form-group">
                        <div className="spare-parts-field">
                          <label className="checkbox-container">
                            <input
                              type="checkbox"
                              checked={closeFormData.spare_parts_required}
                              onChange={(e) => setCloseFormData(prev => ({
                                ...prev,
                                spare_parts_required: e.target.checked
                              }))}
                            />
                            <span>Spare Parts Required</span>
                          </label>
                          <span className="spare-parts-info">Did this repair require spare parts or replacements?</span>
                        </div>
                      </div>
                      <div className="form-group">
                        <label htmlFor="hoursSpent">Hours Spent</label>
                        <input
                          type="number"
                          id="hoursSpent"
                          value={closeFormData.hours_spent}
                          onChange={(e) => setCloseFormData(prev => ({
                            ...prev,
                            hours_spent: e.target.value
                          }))}
                          min="0"
                          step="0.5"
                          placeholder="e.g., 2.5"
                          className="form-input"
                        />
                        <span className="spare-parts-info">How many hours did you spend resolving this complaint?</span>
                      </div>
                      <div className="form-group">
                        <label htmlFor="closingRemarks">Closing Remarks *</label>
                        <textarea
                          id="closingRemarks"
                          value={closeFormData.closing_remarks}
                          onChange={(e) => setCloseFormData(prev => ({
                            ...prev,
                            closing_remarks: e.target.value
                          }))}
                          required
                          rows="3"
                          placeholder="Describe what was repaired, the actions taken, and any recommendations..."
                          className="close-remarks-textarea"
                        />
                        <div className="textarea-char-count">
                          {closeFormData.closing_remarks.length} characters
                        </div>
                      </div>

                      {/* Documents for this complaint only - technicians */}
                      {isTechnician && closeDeviceScanned && device && (
                        <div className="form-section close-complaint-documents">
                          <h3 className="section-title required">Reports & Documents</h3>
                          <p className="form-helper-text">
                            Documents for this complaint only. Upload a PDF/file or take a live photo of a
                            completed closure form. Files from different complaints on this device
                            are listed on the complaint detail page under &quot;Documents from other
                            complaints on this device&quot;.
                          </p>
                          <p className="form-helper-text" style={{ color: '#8a4b00' }}>
                            Upload at least one complaint report (required) before closing. You can only
                            remove reports you upload in this session. If you keep the complaint open,
                            session uploads stay on this complaint—delete them first if you do not want to
                            keep them.
                          </p>
                          <DocumentManager
                            complaintId={complaint.id}
                            token={token}
                            user={user}
                            documentOptions={COMPLAINT_DOCUMENT_OPTIONS}
                            allowedDocumentTypes={complaintReportDocumentTypes}
                            listDocumentType={COMPLAINT_DOCUMENT_TYPE_REPORT}
                            uploadDocumentOptions={COMPLAINT_CLOSE_UPLOAD_DOCUMENT_OPTIONS}
                            permissions={{ canUpload: true, canPreview: true, canDelete: true }}
                            deleteOnlyOwn
                            restrictDeleteToNewSinceInitialLoad
                            enableCameraCapture
                            onPastBaselineDocumentCountChange={setPastBaselineDocumentCount}
                          />
                        </div>
                      )}

                      <div className="form-actions form-actions-close">
                        <button
                          type="button"
                          className="action-btn action-secondary"
                          onClick={handleRequestKeepOpen}
                        >
                          <span className="action-icon">←</span>
                          <span>Keep Open</span>
                        </button>
                        <button
                          type="submit"
                          className="action-btn action-approve"
                          disabled={
                            !closeFormData.closing_remarks.trim() ||
                            (isTechnician && closeDeviceScanned && pastBaselineDocumentCount < 1)
                          }
                          title={
                            isTechnician && closeDeviceScanned && pastBaselineDocumentCount < 1
                              ? 'Upload at least one complaint report before closing'
                              : undefined
                          }
                        >
                          <span className="action-icon">✓</span>
                          <span>Confirm Closure</span>
                        </button>
                      </div>
                    </form>
                  </>
                )}
                </div>
              </div>
            </div>
          </>,
          document.body
        )}

        {/* Cancel Complaint Form Modal - Using ReactDOM.createPortal */}
        {/* CRITICAL: BarcodeScanner MUST be INSIDE the same portal as form-modal */}
        {/* This prevents z-index stacking context issues and ensures proper layering */}
        {showCancelForm && ReactDOM.createPortal(
          <>
            {/* Barcode Scanner - INSIDE the portal, rendered on top */}
            {isTechnician && showCancelDeviceScan && (
              <BarcodeScanner
                onScan={(code) => handleDeviceScan(code, 'cancel')}
                onClose={() => setShowCancelDeviceScan(false)}
                title="Verify Device"
                subtitle="Scan the device QR code to verify before cancelling this complaint."
                expectedDeviceId={complaint.device_identifier}
              />
            )}

            {/* Form Modal - Same portal as scanner */}
            <div className="form-modal-overlay">
              <div className="form-modal form-modal--workflow" onClick={(e) => e.stopPropagation()}>
                <div className="form-modal-header">
                  <h3 className="form-modal-title">⚠️ Cancel Complaint</h3>
                  <ModalCloseButton
                    onClick={() => {
                      setShowCancelForm(false);
                      setCancelDeviceScanned(false);
                    }}
                    title="Close without cancelling"
                  />
                </div>

                <div className="form-modal-content">

                {/* SCAN-FIRST MODE: Only show device info and scan section until device is verified (Technician only) */}
                {isTechnician && !cancelDeviceScanned ? (
                  <>
                    {/* Device Info Section */}
                    <div className="form-section device-info-section">
                      <h3 className="section-title">Device Information</h3>
                      <div className="device-info-display">
                        <div className="info-row">
                          <span className="info-label">Device ID:</span>
                          <span className="info-value">
                            <DeviceIDBadge deviceId={complaint.device_identifier || 'N/A'} size="small" />
                          </span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Device Name:</span>
                          <span className="info-value">{complaint.device_name || 'N/A'}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Complaint Action:</span>
                          <span className="info-value" style={{ color: '#e74c3c', fontWeight: '600' }}>Cancel</span>
                        </div>
                      </div>
                    </div>

                    {/* Device Scan Section - Required First Step */}
                    <div className="form-section scan-section scan-required">
                      <h3 className="section-title">Scan Device to Continue</h3>
                      <p className="form-helper-text">Scan the device QR code to verify physical presence before cancelling the complaint.</p>
                      {scanError && (
                        <div className="scan-error">
                          <p className="error-title">{scanError.title}</p>
                          <p className="error-message">{scanError.message}</p>
                        </div>
                      )}
                      <button
                        type="button"
                        className="action-btn action-primary btn-scan-device"
                        onClick={() => setShowCancelDeviceScan(true)}
                        disabled={isScanning}
                      >
                        <span className="action-icon">📷</span>
                        <span className="action-text">Scan Device</span>
                      </button>
                    </div>

                    {/* Form Actions - Only Cancel available during scan phase */}
                    <div className="form-actions">
                      <button
                        type="button"
                        onClick={() => {
                          setShowCancelForm(false);
                          setCancelDeviceScanned(false);
                        }}
                        className="action-btn action-secondary"
                        disabled={isScanning}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* FORM MODE: Show full cancel form after device is scanned (or for non-technicians) */}

                    {/* Device Verified Status - Only show for technicians who scanned */}
                    {isTechnician && cancelDeviceScanned && (
                      <DeviceVerifiedBanner scannedDeviceId={scannedDeviceId} />
                    )}

                    <div className="cancel-reason-info">
                      <p className="form-warning-enhanced">
                        This action will mark the complaint as cancelled. The record will be preserved for audit purposes and cannot be reopened.
                      </p>
                      <div className="cancel-reason-examples">
                        <p className="examples-label">Common reasons for cancellation:</p>
                        <ul className="examples-list">
                          <li>Duplicate entry</li>
                          <li>Entered in error</li>
                          <li>Issue resolved through other means</li>
                          <li>Device already replaced</li>
                        </ul>
                      </div>
                    </div>

                    <form onSubmit={handleCancelComplaint}>
                      <div className="form-group">
                        <label htmlFor="cancelReason">Reason for Cancellation *</label>
                        <textarea
                          id="cancelReason"
                          value={cancelReason}
                          onChange={(e) => setCancelReason(e.target.value)}
                          required
                          rows="3"
                          placeholder="Please explain why this complaint is being cancelled..."
                          className="cancel-reason-textarea"
                        />
                        <div className="textarea-char-count">
                          {cancelReason.length} characters
                        </div>
                      </div>

                      <div className="form-actions form-actions-cancel">
                        <button
                          type="button"
                          className="action-btn action-secondary"
                          onClick={() => {
                            setShowCancelForm(false);
                            setCancelDeviceScanned(false);
                          }}
                        >
                          <span className="action-icon">←</span>
                          <span>Keep Open</span>
                        </button>
                        <button
                          type="submit"
                          className="action-btn action-reject"
                          disabled={!cancelReason.trim()}
                        >
                          <span className="action-icon">✗</span>
                          <span>Confirm Cancellation</span>
                        </button>
                      </div>
                    </form>
                  </>
                )}
                </div>
              </div>
            </div>
          </>,
          document.body
        )}


        {showStartWorkModal && complaint && ReactDOM.createPortal(
          <>
            {showStartWorkDeviceScan && (
              <BarcodeScanner
                onScan={(code) => handleDeviceScan(code, 'start_work')}
                onClose={() => setShowStartWorkDeviceScan(false)}
                title="Verify Device"
                subtitle="Scan the device QR code to verify before starting work."
                expectedDeviceId={complaint.device_identifier}
              />
            )}

            <div className="form-modal-overlay">
              <div
                className="form-modal start-work-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="start-work-title"
              >
                <div className="form-modal-header">
                  <h3 id="start-work-title" className="form-modal-title">Start Work</h3>
                  <ModalCloseButton
                    onClick={closeStartWorkModal}
                    title="Close without starting work"
                    disabled={isStartingWork}
                  />
                </div>

                <div className="form-modal-content">
                  {!startWorkDeviceScanned ? (
                    <>
                      <div className="form-section device-info-section">
                        <h3 className="section-title">Device Information</h3>
                        <div className="device-info-display">
                          <div className="info-row">
                            <span className="info-label">Device ID:</span>
                            <span className="info-value">
                              <DeviceIDBadge deviceId={complaint.device_identifier || 'N/A'} size="small" />
                            </span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">Device Name:</span>
                            <span className="info-value">{complaint.device_name || 'N/A'}</span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">Complaint Action:</span>
                            <span className="info-value" style={{ color: '#3498db', fontWeight: '600' }}>Start Work</span>
                          </div>
                        </div>
                      </div>

                      <div className="form-section scan-section scan-required">
                        <h3 className="section-title">Scan Device to Continue</h3>
                        <p className="form-helper-text">
                          Scan the device QR code to verify you are at the device before starting work.
                        </p>
                        {scanError && (
                          <div className="scan-error">
                            <p className="error-title">{scanError.title}</p>
                            <p className="error-message">{scanError.message}</p>
                          </div>
                        )}
                        <button
                          type="button"
                          className="action-btn action-primary btn-scan-device"
                          onClick={() => setShowStartWorkDeviceScan(true)}
                          disabled={isScanning}
                        >
                          <span className="action-icon">📷</span>
                          <span className="action-text">Scan Device</span>
                        </button>
                      </div>

                      <div className="form-actions">
                        <button
                          type="button"
                          className="action-btn action-secondary"
                          onClick={closeStartWorkModal}
                          disabled={isScanning}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <DeviceVerifiedBanner scannedDeviceId={scannedDeviceId} />

                      <div className="start-work-summary">
                        <div className="start-work-summary-row">
                          <span className="start-work-summary-label">Current severity</span>
                          <SeverityBadge severity={complaint.severity} size="medium" />
                        </div>
                        <p className={`severity-desc severity-${complaint.severity || 'minor'}`}>
                          {getComplaintSeverityDescription(complaint.severity)}
                        </p>
                      </div>

                      <div className="start-work-info-notice" role="note">
                        <span className="start-work-info-icon" aria-hidden="true">ℹ️</span>
                        <div className="start-work-info-text">
                          <strong>Confirm severity and availability impact.</strong>
                          {' '}
                          Severity defaults from the device&apos;s criticality and can be adjusted after
                          initial diagnosis while the complaint is still open or acknowledged. Once you
                          start work, severity and availability impact are locked and cannot be changed.
                        </div>
                      </div>

                      {scanError && (
                        <div className="scan-error">
                          <p className="error-title">{scanError.title}</p>
                          <p className="error-message">{scanError.message}</p>
                        </div>
                      )}

                      <div className="form-section start-work-impact-section">
                        <label className="info-label">Availability impact</label>
                        <p className="form-helper-text start-work-impact-question">
                          Does this issue affect clinical use or device availability?
                        </p>
                        <div className="start-work-impact-options" role="radiogroup" aria-label="Availability impact">
                          <label className={`start-work-impact-option ${startWorkImpactChoice === true ? 'selected' : ''}`}>
                            <input
                              type="radio"
                              name="start-work-impact"
                              value="yes"
                              checked={startWorkImpactChoice === true}
                              onChange={() => setStartWorkImpactChoice(true)}
                              disabled={isStartingWork}
                            />
                            <span className="start-work-impact-option-content">
                              <span className="start-work-impact-option-title">Yes — impacts availability</span>
                              <span className="start-work-impact-option-desc">
                                Device is unavailable or clinical use is affected.
                              </span>
                            </span>
                          </label>
                          <label className={`start-work-impact-option ${startWorkImpactChoice === false ? 'selected' : ''}`}>
                            <input
                              type="radio"
                              name="start-work-impact"
                              value="no"
                              checked={startWorkImpactChoice === false}
                              onChange={() => setStartWorkImpactChoice(false)}
                              disabled={isStartingWork}
                            />
                            <span className="start-work-impact-option-content">
                              <span className="start-work-impact-option-title">No — does not impact availability</span>
                              <span className="start-work-impact-option-desc">
                                Device remains clinically usable; issue is minor or cosmetic.
                              </span>
                            </span>
                          </label>
                        </div>
                      </div>

                      <div className="form-actions form-actions-close">
                        <button
                          type="button"
                          className="action-btn action-secondary"
                          onClick={closeStartWorkModal}
                          disabled={isStartingWork}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="action-btn action-primary"
                          onClick={handleStartWorkConfirm}
                          disabled={startWorkImpactChoice === null || isStartingWork}
                        >
                          {isStartingWork ? 'Starting…' : 'Start Work'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </>,
          document.body
        )}

        {showEscalateForm && complaint && ReactDOM.createPortal(
          <div className="form-modal-overlay">
            <div
              className="form-modal escalate-vendor-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="escalate-vendor-title"
            >
              <div className="form-modal-header">
                <h3 id="escalate-vendor-title" className="form-modal-title">Escalate to Vendor</h3>
                <ModalCloseButton
                  onClick={closeEscalateForm}
                  title="Close without escalating"
                  disabled={isEscalating}
                />
              </div>

              <div className="form-modal-content">
                <div className="form-section device-info-section">
                  <h3 className="section-title">Complaint Context</h3>
                  <div className="device-info-display">
                    <div className="info-row">
                      <span className="info-label">Complaint ID:</span>
                      <span className="info-value">
                        <ComplaintIDBadge complaintId={complaint.complaint_identifier || 'N/A'} size="small" />
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Device:</span>
                      <span className="info-value">{complaint.device_name || 'N/A'}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Device ID:</span>
                      <span className="info-value">
                        <DeviceIDBadge deviceId={complaint.device_identifier || 'N/A'} size="small" />
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Contract:</span>
                      <span className="info-value contract-info-value">
                        <span className="contract-type-text">{device?.service_contract_type || 'N/A'}</span>
                        {device && getDeviceContractValidityText(device) && (
                          <span className="contract-validity-text">
                            {getDeviceContractValidityText(device)}
                          </span>
                        )}
                        {device?.service_contract_type && (
                          <ContractCoverageBadge
                            status={getDeviceContractCoverageStatus(device)}
                            size="small"
                          />
                        )}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Severity:</span>
                      <span className="info-value">
                        <SeverityBadge severity={complaint.severity} size="small" />
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Availability impact:</span>
                      <span className="info-value">{getAvailabilityImpactLabel(complaint)}</span>
                    </div>
                  </div>
                </div>

                <div className="start-work-info-notice" role="note">
                  <span className="start-work-info-icon" aria-hidden="true">ℹ️</span>
                  <div className="start-work-info-text">
                    Vendor directory is not linked yet. Enter vendor contact details manually.
                    {device?.supplier ? ` Device supplier on record: ${device.supplier}.` : ''}
                  </div>
                </div>

                {escalationFormError && (
                  <div className="scan-error" style={{ marginBottom: '12px' }}>
                    <p className="error-message">{escalationFormError}</p>
                  </div>
                )}

                <div className="form-section">
                  <label className="info-label" htmlFor="escalate-vendor-name">Vendor name (required)</label>
                  <input
                    id="escalate-vendor-name"
                    type="text"
                    className="sidebar-select"
                    style={{ width: '100%', marginTop: '8px' }}
                    value={escalationForm.vendor_name}
                    onChange={(e) => setEscalationForm({ ...escalationForm, vendor_name: e.target.value })}
                    disabled={isEscalating}
                  />
                </div>

                <div className="form-section">
                  <label className="info-label" htmlFor="escalate-contact-person">Contact person</label>
                  <input
                    id="escalate-contact-person"
                    type="text"
                    className="sidebar-select"
                    style={{ width: '100%', marginTop: '8px' }}
                    value={escalationForm.contact_person}
                    onChange={(e) => setEscalationForm({ ...escalationForm, contact_person: e.target.value })}
                    disabled={isEscalating}
                  />
                </div>

                <div className="form-section">
                  <label className="info-label" htmlFor="escalate-vendor-email">Email (required)</label>
                  <input
                    id="escalate-vendor-email"
                    type="email"
                    className="sidebar-select"
                    style={{ width: '100%', marginTop: '8px' }}
                    value={escalationForm.email}
                    onChange={(e) => setEscalationForm({ ...escalationForm, email: e.target.value })}
                    placeholder="e.g., support@vendor.com"
                    disabled={isEscalating}
                  />
                  {escalationForm.email && (
                    <>
                      {escalationEmailValidation.error && (
                        <small className="form-error-text" style={{ color: '#d32f2f' }}>
                          {escalationEmailValidation.error}
                        </small>
                      )}
                      {escalationEmailValidation.isValid && (
                        <small className="form-success-text" style={{ color: '#27ae60' }}>
                          ✓ Valid email address
                        </small>
                      )}
                    </>
                  )}
                </div>

                <div className="form-section">
                  <label className="info-label" htmlFor="escalate-vendor-phone">Phone</label>
                  <div className="phone-input-wrapper">
                    <input
                      id="escalate-vendor-phone"
                      type="tel"
                      className="sidebar-select"
                      style={{ width: '100%', marginTop: '8px' }}
                      value={escalationForm.phone}
                      onChange={(e) => setEscalationForm({
                        ...escalationForm,
                        phone: sanitizeIndianPhoneInput(e.target.value),
                      })}
                      placeholder="e.g., 9876543210"
                      maxLength="10"
                      disabled={isEscalating}
                    />
                    <div className="phone-validation-display">
                      {(() => {
                        const digits = escalationForm.phone ? escalationForm.phone.replace(/\D/g, '') : '';
                        const statusColor = escalationForm.phone && !escalationPhoneValidation.isValid
                          ? '#d32f2f'
                          : digits.length === 10
                            ? '#27ae60'
                            : '#f59e0b';

                        return (
                          <>
                            <span className="digit-count" style={{ color: statusColor }}>
                              {digits.length}/10
                            </span>
                            {escalationForm.phone && (
                              <span className="validation-status" style={{ color: statusColor }}>
                                {!escalationPhoneValidation.isValid ? '✗' : digits.length === 10 ? '✓' : '⚠'}
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  {escalationForm.phone && (
                    <>
                      {escalationPhoneValidation.error && (
                        <small className="form-error-text" style={{ color: '#d32f2f' }}>
                          {escalationPhoneValidation.error}
                        </small>
                      )}
                      {escalationPhoneValidation.isValid && (
                        <small className="form-success-text" style={{ color: '#27ae60' }}>
                          ✓ {formatPhoneNumber(escalationForm.phone)}
                        </small>
                      )}
                    </>
                  )}
                  <small className="form-help-text">Indian format: 10-digit mobile number (must start with 6, 7, 8, or 9)</small>
                </div>

                <div className="form-section">
                  <label className="info-label" htmlFor="escalate-issue-summary">Issue summary for vendor (required)</label>
                  {complaint?.problem_details && (
                    <p className="form-helper-text" style={{ marginTop: '4px' }}>
                      Reporter description (for reference): {complaint.problem_details}
                    </p>
                  )}
                  <textarea
                    id="escalate-issue-summary"
                    className="sidebar-select"
                    style={{ width: '100%', marginTop: '8px', minHeight: '90px', resize: 'vertical' }}
                    placeholder="Summarize the issue for the vendor — symptoms, error codes, and what support is needed."
                    value={escalationForm.issue_summary}
                    onChange={(e) => setEscalationForm({ ...escalationForm, issue_summary: e.target.value })}
                    disabled={isEscalating}
                  />
                </div>

                <div className="form-section">
                  <label className="info-label" htmlFor="escalate-findings">Technician findings / diagnostics (required)</label>
                  <textarea
                    id="escalate-findings"
                    className="sidebar-select"
                    style={{ width: '100%', marginTop: '8px', minHeight: '100px', resize: 'vertical' }}
                    placeholder="What was checked, why vendor is needed, parts/service required"
                    value={escalationForm.technician_findings}
                    onChange={(e) => setEscalationForm({ ...escalationForm, technician_findings: e.target.value })}
                    disabled={isEscalating}
                  />
                </div>

                <div className="form-actions">
                  <button type="button" className="action-btn action-secondary" onClick={closeEscalateForm} disabled={isEscalating}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="action-btn action-primary"
                    onClick={handleEscalateSubmit}
                    disabled={isEscalating || hasInvalidEscalationContact}
                  >
                    {isEscalating ? 'Escalating…' : 'Escalate to Vendor'}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {showResumeConfirm && complaint && ReactDOM.createPortal(
          <div className="form-modal-overlay">
            <div className="form-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <div className="form-modal-header">
                <h3 className="form-modal-title">Resume Work</h3>
                <ModalCloseButton
                  onClick={() => setShowResumeConfirm(false)}
                  title="Close"
                  disabled={isResuming}
                />
              </div>
              <div className="form-modal-content">
                <p className="form-helper-text">
                  Confirm the vendor is onsite and work is resuming. The complaint will move back to In Progress.
                </p>
                <div className="form-actions">
                  <button type="button" className="action-btn action-secondary" onClick={() => setShowResumeConfirm(false)} disabled={isResuming}>
                    Cancel
                  </button>
                  <button type="button" className="action-btn action-primary" onClick={handleResumeWorkConfirm} disabled={isResuming}>
                    {isResuming ? 'Resuming…' : 'Resume Work'}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        <ConfirmationModal
          isOpen={showKeepOpenConfirm}
          entityName={complaint?.complaint_identifier || 'this complaint'}
          entityType="complaint"
          action="keep open"
          actionVerb="keep open"
          customMessage={
            pastBaselineDocumentCount === 1
              ? '1 complaint report uploaded in this session will remain attached to this open complaint. Delete it in the close form first if you do not want to keep it.'
              : `${pastBaselineDocumentCount} complaint reports uploaded in this session will remain attached to this open complaint. Delete them in the close form first if you do not want to keep them.`
          }
          onConfirm={finalizeKeepOpenClose}
          onCancel={() => setShowKeepOpenConfirm(false)}
        />
      </div>
    </div>
  );

  // Return as full page or portal based on isFullPage prop
  return isFullPage ? mainContent : ReactDOM.createPortal(mainContent, document.body);
}

export default ComplaintView;
